/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { getRohlinge, getSchriftarten, getBilder, getHolzAnhaenger, buildAssetUrl } from '../services/api';
import { library, icon } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import * as fontkit from 'fontkit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './UserView.css';

// FontAwesome-Bibliothek initialisieren
library.add(fas, far, fab);

function UserView() {
  const [rohlinge, setRohlinge] = useState([]);
  const [schriftarten, setSchriftarten] = useState([]);
  const [bilder, setBilder] = useState([]);
  const [selectedRohling, setSelectedRohling] = useState(null);
  const [selectedFarbvariante, setSelectedFarbvariante] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeMenu, setActiveMenu] = useState(null);
  const [holzAnhaenger, setHolzAnhaenger] = useState([]);
  const [selectedHolzKategorie, setSelectedHolzKategorie] = useState(null);
  const [selectedHolz, setSelectedHolz] = useState(null);
  const [selectedHolzColor, setSelectedHolzColor] = useState(null);
  const [selectedHolzSvgIndex, setSelectedHolzSvgIndex] = useState(0);
  const [holzName, setHolzName] = useState('');
  const [holzFont, setHolzFont] = useState('');
  const [holzStyle, setHolzStyle] = useState({ bold: false, italic: false });
  const [holzTextTransform, setHolzTextTransform] = useState({ x: 0, y: 0, scale: 1 }); // mm offsets + scale factor
  const [holzTextSelected, setHolzTextSelected] = useState(false);
  const [holzTextInteraction, setHolzTextInteraction] = useState({ dragging: false, resizing: false, handle: null });
  const holzTextDragStartRef = useRef(null);
  const [holzPreviewCache, setHolzPreviewCache] = useState({});
  const [fontsReadyTick, setFontsReadyTick] = useState(0);

  const hideHolzMenuOnThisHost = (() => {
    const forced = String(process.env.REACT_APP_HIDE_HOLZ_MENU || '').toLowerCase() === 'true';
    if (forced) return true;
    if (typeof window === 'undefined') return false;
    const host = String(window.location?.hostname || '').toLowerCase();
    return host === 'vercel.app' || host.endsWith('.vercel.app') || host.endsWith('.now.sh');
  })();

  // Toggle-Funktionen für Holz-Formatierung
  const toggleHolzFormatting = (formatType) => {
    setHolzStyle(prev => ({
      ...prev,
      [formatType]: !prev[formatType]
    }));
  };

  const isHolzFormatActive = (formatType) => {
    return holzStyle[formatType] || false;
  };

  const canvasRefFront = useRef(null);
  const canvasRefBack = useRef(null);
  const holzCanvasRef = useRef(null);
  const renderQueueRef = useRef({ front: false, back: false, scheduled: false });

  // State für Drag & Drop
  const [draggingImage, setDraggingImage] = useState({ front: false, back: false });
  const [dragStart, setDragStart] = useState({ front: { x: 0, y: 0 }, back: { x: 0, y: 0 } });

  // Hilfsfunktion: leere Text-Konfiguration erzeugen
  const createEmptyTextConfig = (id) => ({
    id,
    text: '',
    font: 'Arial',
    size: 48,
    x: 0,
    y: 0,
    scale: 1,
    rotation: 0,
    alignHorizontal: 'center',
    alignVertical: 'center',
    bold: false,
    italic: false,
    underline: false
  });

  // State für Text-Interaktion
  const [textInteraction, setTextInteraction] = useState({
    front: { dragging: false, resizing: false, rotating: false, handle: null },
    back: { dragging: false, resizing: false, rotating: false, handle: null }
  });
  const [textDragStart, setTextDragStart] = useState({ front: { x: 0, y: 0 }, back: { x: 0, y: 0 } });

  const [textConfig, setTextConfig] = useState({
    front: [
      createEmptyTextConfig('1'),
      createEmptyTextConfig('2'),
      createEmptyTextConfig('3')
    ],
    back: [
      createEmptyTextConfig('1'),
      createEmptyTextConfig('2'),
      createEmptyTextConfig('3')
    ]
  });

  // State for selected text index (for interaction)
  const [selectedTextIndex, setSelectedTextIndex] = useState({
    front: null,
    back: null
  });

  // Sichtbarkeit der Textfelder (Text 1 immer sichtbar, 2/3 optional)
  const [visibleTextFields, setVisibleTextFields] = useState({
    front: [true, false, false],
    back: [true, false, false]
  });

  // Font-Stack mit Fallbacks aufbauen
  const getFontStack = (fontName) => {
    const entry = schriftarten.find(s => s.name === fontName);
    const stack = [
      entry?.name || fontName || 'Arial',
      ...(entry?.fallbacks || []),
      'Arial',
      'sans-serif'
    ];
    return stack
      .filter(Boolean)
      .map(f => (f.includes(' ') ? `'${f}'` : f))
      .join(', ');
  };

  // Bild-Konfiguration - Array von Bildern pro Seite
  const [images, setImages] = useState({
    front: [],
    back: []
  });

  // Ausgewähltes Bild (für Bearbeitung)
  const [selectedImageId, setSelectedImageId] = useState({
    front: null,
    back: null
  });

  // Raster-Sichtbarkeit
  const [showGrid, setShowGrid] = useState({
    front: true,
    back: true
  });
  const fontArrayBufferCacheRef = useRef(new Map()); // Cache: font file ArrayBuffers
  const fontkitCacheRef = useRef(new Map()); // Cache: parsed fontkit fonts
  const imageCacheRef = useRef(new Map()); // Cache für gefärbte SVGs/Bilder

  // Für Rich-Text-Formatierung - Arrays for 3 texts
  const [textareaRefs] = useState({
    front: [React.createRef(), React.createRef(), React.createRef()],
    back: [React.createRef(), React.createRef(), React.createRef()]
  });

  // Bestellformular-Modal
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderFormData, setOrderFormData] = useState({
    customerName: '',
    customerEmail: '',
    isExistingCustomer: true,
    street: '',
    postalCode: '',
    city: '',
    confirmOrder: false,
    acceptPrivacy: false
  });
  const [orderLoading, setOrderLoading] = useState(false);

  // Multi-Rohling-Bestellung
  const [savedOrders, setSavedOrders] = useState([]);

  // Funktion: Gespeicherte Bestellung löschen
  const handleRemoveSavedOrder = (index) => {
    if (window.confirm('Möchten Sie diesen Rohling wirklich aus der Bestellung entfernen?')) {
      setSavedOrders(prev => prev.filter((_, i) => i !== index));
      showMessage('success', 'Rohling aus Bestellung entfernt');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, []);

  // Formulardaten aus sessionStorage wiederherstellen, wenn Modal geöffnet wird
  useEffect(() => {
    if (showOrderModal) {
      const savedData = sessionStorage.getItem('orderFormData');
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setOrderFormData(parsedData);
          sessionStorage.removeItem('orderFormData');
        } catch (e) {
          console.error('Fehler beim Laden der Formulardaten:', e);
        }
      }
    }
  }, [showOrderModal]);

  // Registriere @font-face für hochgeladene Webfonts
  useEffect(() => {
    const styleId = 'dynamic-font-faces';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    const rules = [];
    schriftarten.forEach((font) => {
      if (!Array.isArray(font.sources)) return;
      font.sources.forEach((src) => {
        if (!src.path) return;
        const url = buildAssetUrl(src.path);
        const format = src.format || (src.path.toLowerCase().endsWith('.woff2') ? 'woff2' : 'woff');
        const weight = src.weight || 400;
        const style = src.style || 'normal';
        rules.push(`@font-face { font-family: '${font.name}'; src: url('${url}') format('${format}'); font-weight: ${weight}; font-style: ${style}; font-display: swap; }`);
      });
    });

    styleEl.textContent = rules.join('\n');
  }, [schriftarten]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scheduleRender = (side) => {
    if (!side) return;
    renderQueueRef.current[side] = true;
    if (renderQueueRef.current.scheduled) return;
    renderQueueRef.current.scheduled = true;
    requestAnimationFrame(() => {
      renderQueueRef.current.scheduled = false;
      const queue = { ...renderQueueRef.current };
      renderQueueRef.current.front = false;
      renderQueueRef.current.back = false;
      if (queue.front) {
        renderMockup('front');
      }
      if (queue.back) {
        renderMockup('back');
      }
    });
  };

  // Fonts laden (Canvas soll warten bis Webfonts bereit sind)
  useEffect(() => {
    if (!document.fonts || !schriftarten || schriftarten.length === 0) return;
    const entries = [];
    schriftarten.forEach((font) => {
      if (!Array.isArray(font.sources)) return;
      font.sources.forEach((src) => {
        const weight = src.weight || 400;
        const style = src.style || 'normal';
        entries.push(`${style} ${weight} 16px '${font.name}'`);
      });
    });
    if (entries.length === 0) return;
    Promise.all(entries.map(f => document.fonts.load(f).catch(() => null)))
      .then(() => setFontsReadyTick(Date.now()))
      .catch(() => {});
  }, [schriftarten]);

  useEffect(() => {
    if (selectedRohling && selectedFarbvariante) {
      scheduleRender('front');
      if (selectedRohling.beidseitig) {
        scheduleRender('back');
      }
    }
  }, [selectedRohling, selectedFarbvariante, textConfig, images, selectedImageId, showGrid, textInteraction, fontsReadyTick]);

  // useEffect für Wheel-Event-Listener mit passive: false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const frontCanvas = canvasRefFront.current;
    const backCanvas = canvasRefBack.current;

    const wheelHandlerFront = (e) => handleImageWheel('front', e);
    const wheelHandlerBack = (e) => handleImageWheel('back', e);

    if (frontCanvas && selectedImageId.front) {
      frontCanvas.addEventListener('wheel', wheelHandlerFront, { passive: false });
    }
    if (backCanvas && selectedImageId.back && selectedRohling?.beidseitig) {
      backCanvas.addEventListener('wheel', wheelHandlerBack, { passive: false });
    }

    return () => {
      if (frontCanvas) {
        frontCanvas.removeEventListener('wheel', wheelHandlerFront);
      }
      if (backCanvas) {
        backCanvas.removeEventListener('wheel', wheelHandlerBack);
      }
    };
  }, [selectedImageId.front, selectedImageId.back, selectedRohling]);

  const normalizeFarbvarianten = (rohling) => {
    if (rohling.farbvarianten && rohling.farbvarianten.length > 0) {
      return rohling.farbvarianten;
    }

    if (rohling.rohlingfarben && rohling.rohlingfarben.length > 0) {
      return rohling.rohlingfarben.map((farbe, index) => ({
        ...farbe,
        id: farbe.id || `${rohling.id || 'rohling'}-rohlingfarbe-${index}`,
        farbe: farbe.name || farbe.farbe || 'Farbe',
        farbeHex: farbe.hexColor || farbe.farbeHex || farbe.color || '#cccccc',
        mode: 'color',
        frontImage: null,
        backImage: null
      }));
    }

    return [];
  };

  const normalizeRohlinge = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map(rohling => ({
      ...rohling,
      farbvarianten: normalizeFarbvarianten(rohling)
    }));
  };

  const normalizeHolz = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map(item => {
      const svgPaths = Array.isArray(item?.svgPaths)
        ? item.svgPaths.filter(p => typeof p === 'string' && p.trim()).map(p => p.trim())
        : (item?.svgPath ? [item.svgPath] : []);
      const svgPath = svgPaths[0] || item?.svgPath || null;

      return ({
        ...item,
        svgPaths,
        svgPath,
        farben: Array.isArray(item.farben) ? item.farben.filter(f => f && f.hexColor) : [],
        gravurfarbe: item.gravurfarbe || '#000000'
      });
    });
  };

  const getHolzColor = () => {
    if (!selectedHolz) return '#d1b08c';
    const palette = Array.isArray(selectedHolz.farben) ? selectedHolz.farben : [];
    const selected = palette.find(f => f.id === selectedHolzColor);
    return selected?.hexColor || palette[0]?.hexColor || '#d1b08c';
  };

  const getHolzSvgPaths = (item) => {
    const raw = Array.isArray(item?.svgPaths)
      ? item.svgPaths
      : (item?.svgPath ? [item.svgPath] : []);
    return raw.filter(p => typeof p === 'string' && p.trim()).map(p => p.trim());
  };

  const getSelectedHolzSvgPath = () => {
    const paths = getHolzSvgPaths(selectedHolz);
    if (paths.length === 0) return null;
    const idxRaw = Number.isFinite(selectedHolzSvgIndex) ? selectedHolzSvgIndex : 0;
    const idx = Math.min(Math.max(idxRaw, 0), paths.length - 1);
    return paths[idx] || paths[0] || null;
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const getHolzTextRenderState = (ctx, { scalePxPerMm } = {}) => {
    if (!ctx || !selectedHolz || !holzName) return null;

    const baseWidth = selectedHolz.width || 100;
    const baseHeight = selectedHolz.height || 100;
    const scale = Number.isFinite(scalePxPerMm) ? scalePxPerMm : 1;

    const areaPx = {
      x: (selectedHolz.gravurX || 0) * scale,
      y: (selectedHolz.gravurY || 0) * scale,
      width: (selectedHolz.gravurWidth || baseWidth) * scale,
      height: (selectedHolz.gravurHeight || baseHeight) * scale
    };

    const fontBase = `${holzStyle.italic ? 'italic ' : ''}${holzStyle.bold ? 'bold ' : ''}`;
    const fontFamily = getFontStack(holzFont || 'Arial');
    const paddingPx = Math.max(areaPx.height * 0.02, 1);

    let fitFontSizePx = areaPx.height;
    while (fitFontSizePx > 8) {
      ctx.font = `${fontBase}${fitFontSizePx}px ${fontFamily}`;
      const textWidth = ctx.measureText(holzName).width;
      if (textWidth <= areaPx.width * 0.9 && fitFontSizePx <= areaPx.height * 0.9) {
        break;
      }
      fitFontSizePx -= 1;
    }

    const scaleFactor = clamp(Number(holzTextTransform?.scale) || 1, 0.1, 5);
    const fontSizePx = Math.max(fitFontSizePx * scaleFactor, 6);
    ctx.font = `${fontBase}${fontSizePx}px ${fontFamily}`;

    const metrics = ctx.measureText(holzName);
    const textWidthPx = metrics.width || 0;
    const ascent = metrics.actualBoundingBoxAscent || fontSizePx * 0.8;
    const descent = metrics.actualBoundingBoxDescent || fontSizePx * 0.2;

    const offsetXpx = (Number(holzTextTransform?.x) || 0) * scale;
    const offsetYpx = (Number(holzTextTransform?.y) || 0) * scale;

    const x = areaPx.x + areaPx.width / 2 + offsetXpx;
    const y = areaPx.y + areaPx.height - paddingPx + offsetYpx;

    const bounds = {
      x: x - textWidthPx / 2,
      y: y - ascent,
      width: textWidthPx,
      height: ascent + descent,
      centerX: x,
      centerY: y - ascent / 2 + descent / 2
    };

    return {
      x,
      y,
      fontBase,
      fontFamily,
      fontSizePx,
      paddingPx,
      bounds
    };
  };

  const getHolzTextHandleAtPoint = ({ canvasX, canvasY, ctx, scalePxPerMm }) => {
    const state = getHolzTextRenderState(ctx, { scalePxPerMm });
    if (!state?.bounds) return null;

    const handleSize = 12;
    const bx = state.bounds.x;
    const by = state.bounds.y;
    const bw = state.bounds.width;
    const inBox = canvasX >= bx && canvasX <= (bx + bw) && canvasY >= by && canvasY <= (by + state.bounds.height);

    const handleX = bx + bw;
    const handleY = by;
    const inHandle = Math.abs(canvasX - handleX) <= handleSize && Math.abs(canvasY - handleY) <= handleSize;

    if (inHandle) return { handle: 'resize-tr', state };
    if (inBox) return { handle: 'move', state };
    return null;
  };

  const mimeForFontFormat = (format) => {
    const f = String(format || '').toLowerCase();
    if (f === 'woff2') return 'font/woff2';
    if (f === 'woff') return 'font/woff';
    if (f === 'ttf') return 'font/ttf';
    if (f === 'otf') return 'font/otf';
    return 'application/octet-stream';
  };

  const base64FromArrayBuffer = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const pickFontSource = (entry, { bold, italic } = {}) => {
    const sources = Array.isArray(entry?.sources) ? entry.sources : [];
    if (sources.length === 0) return null;
    const targetWeight = bold ? 700 : 400;
    const targetStyle = italic ? 'italic' : 'normal';

    let best = sources[0];
    let bestScore = Infinity;
    for (const src of sources) {
      const w = Number.isFinite(src?.weight) ? src.weight : 400;
      const s = src?.style || 'normal';
      const score = Math.abs(w - targetWeight) + (s === targetStyle ? 0 : 1000);
      if (score < bestScore) {
        bestScore = score;
        best = src;
      }
    }
    return best;
  };

  const fetchFontArrayBuffer = async (fontPath) => {
    if (!fontPath) return null;
    const url = buildAssetUrl(fontPath);
    if (!url) return null;
    if (fontArrayBufferCacheRef.current.has(url)) {
      return fontArrayBufferCacheRef.current.get(url);
    }
    const res = await fetch(url);
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    fontArrayBufferCacheRef.current.set(url, buf);
    return buf;
  };

  const getFontkitFont = async ({ fontName, bold, italic } = {}) => {
    if (!fontName) return null;
    const entry = schriftarten.find(s => s.name === fontName);
    const src = pickFontSource(entry, { bold, italic });
    const fontPath = src?.path;
    if (!fontPath) return null;

    const cacheKey = `${fontName}|${src?.path || ''}|${src?.weight || ''}|${src?.style || ''}`;
    if (fontkitCacheRef.current.has(cacheKey)) {
      return fontkitCacheRef.current.get(cacheKey);
    }

    const buf = await fetchFontArrayBuffer(fontPath);
    if (!buf) return null;

    try {
      const font = fontkit.create(new Uint8Array(buf));
      fontkitCacheRef.current.set(cacheKey, font);
      return font;
    } catch {
      return null;
    }
  };

  const measureTextWidthWithFont = (font, text, fontSize) => {
    if (!font || !text) return 0;
    const run = font.layout(text);
    const unitsPerEm = font.unitsPerEm || 1000;
    const scale = fontSize / unitsPerEm;
    const advanceUnits = (run.positions || []).reduce((sum, p) => sum + (p.xAdvance || 0), 0);
    return advanceUnits * scale;
  };

  const buildTextPathsWithFont = ({
    font,
    text,
    fontSize,
    x,
    y,
    anchor = 'start',
    fill = '#000000',
    stroke = null,
    strokeWidth = null,
    strokeLinejoin = 'round'
  }) => {
    if (!font || !text) return '';
    const run = font.layout(text);
    const unitsPerEm = font.unitsPerEm || 1000;
    const scale = fontSize / unitsPerEm;
    const advance = (run.positions || []).reduce((sum, p) => sum + (p.xAdvance || 0), 0) * scale;

    let startX = x;
    if (anchor === 'middle') startX = x - advance / 2;
    if (anchor === 'end') startX = x - advance;

    let cursorX = startX;
    const baselineY = y;
    const parts = [];

    (run.glyphs || []).forEach((glyph, idx) => {
      const pos = run.positions?.[idx] || {};
      const d = glyph?.path?.toSVG ? glyph.path.toSVG() : '';
      if (!d) {
        cursorX += (pos.xAdvance || 0) * scale;
        return;
      }
      const dx = cursorX + (pos.xOffset || 0) * scale;
      const dy = baselineY - (pos.yOffset || 0) * scale;
      const strokeAttrs = stroke && strokeWidth
        ? ` stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="${strokeLinejoin}" vector-effect="non-scaling-stroke"`
        : '';
      parts.push(`<path d="${d}" transform="translate(${dx} ${dy}) scale(${scale} ${-scale})" fill="${fill}"${strokeAttrs} />`);
      cursorX += (pos.xAdvance || 0) * scale;
    });

    return parts.join('');
  };

  const buildEmbeddedFontFaceCss = async (fontUsages) => {
    const rules = [];
    const seen = new Set();
    const usages = Array.isArray(fontUsages) ? fontUsages : [];

    for (const usage of usages) {
      const fontName = usage?.fontName;
      if (!fontName) continue;
      const entry = schriftarten.find(s => s.name === fontName);
      const sources = Array.isArray(entry?.sources) ? entry.sources : [];
      if (sources.length === 0) continue;

      const familyRaw = entry?.name || fontName;
      const familyCss = familyRaw.includes(' ') ? `'${familyRaw}'` : familyRaw;

      for (const src of sources) {
        const path = src?.path;
        if (!path) continue;
        const weight = Number.isFinite(src?.weight) ? src.weight : 400;
        const style = src?.style || 'normal';
        const key = `${familyCss}|${path}|${weight}|${style}`;
        if (seen.has(key)) continue;
        seen.add(key);

        try {
          const buf = await fetchFontArrayBuffer(path);
          if (!buf) continue;
          const base64 = base64FromArrayBuffer(buf);
          const mime = mimeForFontFormat(src?.format);
          const format = String(src?.format || '').toLowerCase() || 'woff2';
          rules.push(
            `@font-face{font-family:${familyCss};src:url(data:${mime};base64,${base64}) format('${format}');font-weight:${weight};font-style:${style};}`
          );
        } catch {
          // ignore
        }
      }
    }

    return rules.join('\n');
  };

  const buildHolzLaserSvg = async () => {
    const selectedSvgPath = getSelectedHolzSvgPath();
    if (!selectedSvgPath) return null;

    if (document?.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // ignore
      }
    }

    const laserColor = '#ff0000';
    const width = selectedHolz.width || 100;
    const height = selectedHolz.height || 100;

    const svgUrl = buildAssetUrl(selectedSvgPath);
    const res = await fetch(svgUrl);
    if (!res.ok) return null;
    const rawSvgText = await res.text();

    const parseSvg = (svgText) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return null;
      const svgEl = doc.querySelector('svg');
      if (!svgEl) return null;

      const serializer = new XMLSerializer();
      let inner = '';
      svgEl.childNodes.forEach(node => {
        inner += serializer.serializeToString(node);
      });

      const viewBox = svgEl.getAttribute('viewBox');
      return { viewBox, inner };
    };

    const parsed = parseSvg(rawSvgText);
    if (!parsed) return null;

    const shapeInner = parsed.inner;

    const vb = (() => {
      if (!parsed.viewBox) return { minX: 0, minY: 0, vbW: width, vbH: height };
      const parts = parsed.viewBox.split(/[\s,]+/).map(Number).filter(n => Number.isFinite(n));
      if (parts.length !== 4) return { minX: 0, minY: 0, vbW: width, vbH: height };
      return { minX: parts[0], minY: parts[1], vbW: parts[2], vbH: parts[3] };
    })();

    const scale = Math.min(width / vb.vbW, height / vb.vbH);
    const offsetX = (width - vb.vbW * scale) / 2;
    const offsetY = (height - vb.vbH * scale) / 2;
    const shapeTransform = `translate(${offsetX} ${offsetY}) scale(${scale}) translate(${-vb.minX} ${-vb.minY})`;

    const fontEntry = schriftarten.find(s => s.name === (holzFont || 'Arial'));
    const buildFontFaceCss = async () => {
      const sources = Array.isArray(fontEntry?.sources) ? fontEntry.sources : [];
      if (sources.length === 0) return '';

      const mimeFor = (format) => {
        const f = String(format || '').toLowerCase();
        if (f === 'woff2') return 'font/woff2';
        if (f === 'woff') return 'font/woff';
        if (f === 'ttf') return 'font/ttf';
        if (f === 'otf') return 'font/otf';
        return 'application/octet-stream';
      };

      const base64FromArrayBuffer = (buffer) => {
        const bytes = new Uint8Array(buffer);
        const chunkSize = 0x8000;
        let binary = '';
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
      };

      const familyRaw = fontEntry?.name || (holzFont || 'Arial');
      const familyCss = familyRaw.includes(' ') ? `'${familyRaw}'` : familyRaw;

      const rules = [];
      for (const src of sources) {
        const path = src?.path;
        if (!path) continue;

        try {
          const fontRes = await fetch(buildAssetUrl(path));
          if (!fontRes.ok) continue;
          const buf = await fontRes.arrayBuffer();
          const base64 = base64FromArrayBuffer(buf);
          const mime = mimeFor(src?.format);
          const format = String(src?.format || '').toLowerCase() || 'woff2';
          const weight = Number.isFinite(src?.weight) ? src.weight : 400;
          const style = src?.style || 'normal';

          rules.push(
            `@font-face{font-family:${familyCss};src:url(data:${mime};base64,${base64}) format('${format}');font-weight:${weight};font-style:${style};}`
          );

          // Wenn nur "normal" vorhanden ist, sorge dafÃ¼r, dass bold/italic nicht auf Fallback-Schriften springt.
          if (holzStyle?.bold && weight !== 700) {
            rules.push(
              `@font-face{font-family:${familyCss};src:url(data:${mime};base64,${base64}) format('${format}');font-weight:700;font-style:${style};}`
            );
          }
          if (holzStyle?.italic && style !== 'italic') {
            rules.push(
              `@font-face{font-family:${familyCss};src:url(data:${mime};base64,${base64}) format('${format}');font-weight:${weight};font-style:italic;}`
            );
          }
        } catch {
          // ignore failed font embedding; we'll still output SVG with normal font-family
        }
      }

      return rules.join('\n');
    };

    const fontFaceCss = await buildFontFaceCss();

    const safeName = (holzName || '').trim();
    const hasName = safeName.length > 0;
    const gravurX = Number.isFinite(selectedHolz.gravurX) ? selectedHolz.gravurX : 0;
    const gravurY = Number.isFinite(selectedHolz.gravurY) ? selectedHolz.gravurY : 0;
    const gravurW = Number.isFinite(selectedHolz.gravurWidth) ? selectedHolz.gravurWidth : width;
    const gravurH = Number.isFinite(selectedHolz.gravurHeight) ? selectedHolz.gravurHeight : height;

    const computeFontSize = () => {
      if (!hasName) return 0;
      const pxPerUnit = 10;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const fontBase = `${holzStyle.italic ? 'italic ' : ''}${holzStyle.bold ? 'bold ' : ''}`;
      const fontFamily = getFontStack(holzFont || 'Arial');
      let size = gravurH * 0.9;
      while (size > 1) {
        ctx.font = `${fontBase}${Math.max(size, 1) * pxPerUnit}px ${fontFamily}`;
        const textWidth = (ctx.measureText(safeName).width || 0) / pxPerUnit;
        if (textWidth <= gravurW * 0.9 && size <= gravurH * 0.9) break;
        size -= 0.5;
      }
      return Math.max(size, 1);
    };

    const fontSize = computeFontSize() * clamp(Number(holzTextTransform?.scale) || 1, 0.1, 5);
    const padding = Math.max(gravurH * 0.02, 0.2);
    const textX = gravurX + gravurW / 2 + (Number(holzTextTransform?.x) || 0);
    const textY = gravurY + gravurH - padding + (Number(holzTextTransform?.y) || 0);
    const textFontFamily = getFontStack(holzFont || 'Arial');
    const textFontWeight = holzStyle.bold ? 'bold' : 'normal';
    const textFontStyle = holzStyle.italic ? 'italic' : 'normal';

    let nameMarkup = '';
    if (hasName) {
      const font = await getFontkitFont({ fontName: holzFont || 'Arial', bold: holzStyle.bold, italic: holzStyle.italic });
      if (font) {
        const entry = schriftarten.find(s => s.name === (holzFont || 'Arial'));
        const sources = Array.isArray(entry?.sources) ? entry.sources : [];
        const targetStyle = holzStyle.italic ? 'italic' : 'normal';
        const hasRealBoldForStyle = sources.some(s => (s?.style || 'normal') === targetStyle && (Number.isFinite(s?.weight) ? s.weight : 400) >= 600);
        const needsSyntheticBold = holzStyle.bold && !hasRealBoldForStyle;
        const syntheticBoldStrokeWidth = needsSyntheticBold ? Math.min(Math.max(fontSize * 0.06, 0.12), 0.6) : null;

        const paths = buildTextPathsWithFont({
          font,
          text: safeName,
          fontSize,
          x: textX,
          y: textY,
          anchor: 'middle',
          fill: laserColor,
          stroke: needsSyntheticBold ? laserColor : null,
          strokeWidth: syntheticBoldStrokeWidth
        });
        nameMarkup = `<g id="holz-text">${paths}</g>`;
      } else {
        nameMarkup = `<text x="${textX}" y="${textY}" text-anchor="middle" font-family="${textFontFamily}" font-size="${fontSize}" font-weight="${textFontWeight}" font-style="${textFontStyle}" fill="${laserColor}">${escapeXml(safeName)}</text>`;
      }
    }

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}mm" height="${height}mm" viewBox="0 0 ${width} ${height}">
  <defs>
    <style><![CDATA[
      ${fontFaceCss}
    ]]></style>
  </defs>
  <g id="holz-shape" transform="${shapeTransform}" fill="none" stroke="${laserColor}">
    ${shapeInner}
  </g>
  ${nameMarkup}
</svg>`;
  };

  const escapeXml = (value) => {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const buildDogtagLaserSvg = async (side) => {
    if (!selectedRohling) return null;

    if (document?.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // ignore
      }
    }

    const widthMM = selectedRohling.breite || 30;
    const heightMM = selectedRohling.hoehe || 40;
    const mockupCanvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    const mmToPixel = mockupCanvas?.width ? (mockupCanvas.width / widthMM) : 10;
    const pxToMm = 1 / mmToPixel;
    const measureCtx = document.createElement('canvas').getContext('2d');

    const gravurfarbeMap = {
      gray: '#808080',
      grey: '#808080',
      black: '#000000'
    };
    const gravurfarbe = gravurfarbeMap[selectedRohling.gravurfarbe] || selectedRohling.gravurfarbe || '#000000';

    const parseSvg = (svgText) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return null;
      const svgEl = doc.querySelector('svg');
      if (!svgEl) return null;

      const serializer = new XMLSerializer();
      let inner = '';
      svgEl.childNodes.forEach(node => {
        inner += serializer.serializeToString(node);
      });

      const viewBox = svgEl.getAttribute('viewBox');
      return { viewBox, inner };
    };

    const inlineSvgAsset = async ({ assetPath, x, y, width, height, rotation, centerX, centerY, color, mode = 'fill' }) => {
      const url = buildAssetUrl(assetPath);
      if (!url) return null;
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const raw = await res.text();
        const parsed = parseSvg(raw);
        if (!parsed) return null;

        const vb = (() => {
          if (!parsed.viewBox) return { minX: 0, minY: 0, vbW: width, vbH: height };
          const parts = parsed.viewBox.split(/[\s,]+/).map(Number).filter(n => Number.isFinite(n));
          if (parts.length !== 4) return { minX: 0, minY: 0, vbW: width, vbH: height };
          return { minX: parts[0], minY: parts[1], vbW: parts[2], vbH: parts[3] };
        })();

        const scale = Math.min(width / vb.vbW, height / vb.vbH);
        const offsetX = (width - vb.vbW * scale) / 2;
        const offsetY = (height - vb.vbH * scale) / 2;
        const placeTransform = `translate(${x + offsetX} ${y + offsetY}) scale(${scale}) translate(${-vb.minX} ${-vb.minY})`;

        const paintAttrs = mode === 'stroke'
          ? `fill="none" stroke="${color}"`
          : `fill="${color}" stroke="${color}"`;

        const innerGroup = `<g transform="${placeTransform}" ${paintAttrs}>${parsed.inner}</g>`;
        if (rotation) {
          return `<g transform="rotate(${rotation} ${centerX} ${centerY})">${innerGroup}</g>`;
        }
        return innerGroup;
      } catch {
        return null;
      }
    };

    const elements = [];

    // Fonts for fallback <text> (and for editors importing the SVG)
    const usedFonts = new Set();
    (textConfig?.[side] || []).forEach(cfg => {
      if (cfg?.font) usedFonts.add(cfg.font);
    });
    const fontFaceCss = await buildEmbeddedFontFaceCss([...usedFonts].map(fontName => ({ fontName })));

    // Rohling-Outline als Vektor (wenn masterSvg ein SVG ist)
    if (typeof selectedRohling.masterSvg === 'string' && selectedRohling.masterSvg.toLowerCase().endsWith('.svg')) {
      const inlinedShape = await inlineSvgAsset({
        assetPath: selectedRohling.masterSvg,
        x: 0,
        y: 0,
        width: widthMM,
        height: heightMM,
        rotation: 0,
        centerX: 0,
        centerY: 0,
        color: gravurfarbe,
        mode: 'stroke'
      });
      if (inlinedShape) {
        elements.push(`<g id="rohling-shape">${inlinedShape}</g>`);
      }
    }

    // Icons / Bilder
    for (const img of (images?.[side] || [])) {
      const bild = bilder.find(b => b.id === img.bildId);
      if (!bild) continue;

      const imgWidth = (512 * img.scale) * pxToMm;
      const imgHeight = (512 * img.scale) * pxToMm;
      const imgX = img.x * pxToMm;
      const imgY = img.y * pxToMm;
      const centerX = imgX + imgWidth / 2;
      const centerY = imgY + imgHeight / 2;
      const rotation = img.rotation || 0;

      if (bild.type === 'fontawesome') {
        const prefix = bild.iconStyle === 'brands' ? 'fab' : bild.iconStyle === 'regular' ? 'far' : 'fas';
        const iconObj = icon({ prefix, iconName: bild.iconName });
        if (!iconObj) continue;
        const iconW = iconObj.icon[0];
        const iconH = iconObj.icon[1];
        const pathD = iconObj.icon[4];
        const rot = rotation ? ` transform="rotate(${rotation} ${centerX} ${centerY})"` : '';
        elements.push(
          `<g${rot}><svg x="${imgX}" y="${imgY}" width="${imgWidth}" height="${imgHeight}" viewBox="0 0 ${iconW} ${iconH}" preserveAspectRatio="xMidYMid meet"><path d="${pathD}" fill="${gravurfarbe}" /></svg></g>`
        );
        continue;
      }

      const assetPath = bild.path;
      if (typeof assetPath === 'string' && assetPath.toLowerCase().endsWith('.svg')) {
        const inlined = await inlineSvgAsset({
          assetPath,
          x: imgX,
          y: imgY,
          width: imgWidth,
          height: imgHeight,
          rotation,
          centerX,
          centerY,
          color: gravurfarbe
        });
        if (inlined) {
          elements.push(inlined);
          continue;
        }
      }

      const href = buildAssetUrl(assetPath);
      const rot = rotation ? ` transform="rotate(${rotation} ${centerX} ${centerY})"` : '';
      elements.push(`<image href="${href}" x="${imgX}" y="${imgY}" width="${imgWidth}" height="${imgHeight}"${rot} />`);
    }

    // Texte
    for (const config of (textConfig?.[side] || [])) {
      if (!config?.text || config.text.trim() === '') continue;

      const lines = config.text.split('\n');
      const fontSizePx = config.size * config.scale;
      const fontSizeMm = fontSizePx * pxToMm;
      const lineHeightMm = fontSizeMm * 1.2;
      const totalTextHeightMm = lines.length * lineHeightMm;

      const entry = schriftarten.find(s => s.name === config.font);
      const sources = Array.isArray(entry?.sources) ? entry.sources : [];
      const font = await getFontkitFont({ fontName: config.font, bold: config.bold, italic: config.italic });
      const usePaths = Boolean(font);

      const parsedLines = lines.map(line => parseFormattedText(line, config));
      const lineWidths = parsedLines.map((parts) => (
        parts.reduce((sum, part) => {
          if (usePaths) return sum + measureTextWidthWithFont(font, part.text, fontSizeMm);

          const fontFamily = getFontStack(config.font);
          const fontStyleStr = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSizePx}px ${fontFamily}`;
          measureCtx.font = fontStyleStr;
          const measuredPx = measureCtx.measureText(part.text).width;
          return sum + (measuredPx * pxToMm);
        }, 0)
      ));
      const maxWidth = lineWidths.reduce((m, w) => Math.max(m, w), 0);

      const computeLineStartX = (lineWidth) => {
        const align = config.alignHorizontal || 'center';
        if (align === 'center') return -(lineWidth / 2);
        if (align === 'right') return (maxWidth / 2) - lineWidth;
        return -(maxWidth / 2); // left
      };

      let baseX = 0;
      let baseY = 0;
      if (config.alignVertical === 'center') {
        baseY = (heightMM - totalTextHeightMm) / 2;
      } else if (config.alignVertical === 'bottom') {
        baseY = heightMM - totalTextHeightMm;
      }

      if (config.alignHorizontal === 'center') {
        baseX = (widthMM - maxWidth) / 2;
      } else if (config.alignHorizontal === 'right') {
        baseX = widthMM - maxWidth;
      }

      const centerX = baseX + maxWidth / 2;
      const centerY = baseY + totalTextHeightMm / 2;
      const offsetX = config.x || 0; // mm
      const offsetY = config.y || 0; // mm
      const blockTransform = `translate(${centerX + offsetX} ${centerY + offsetY}) rotate(${config.rotation || 0})`;

      if (usePaths) {
        let lineYStart = -(totalTextHeightMm / 2);
        let inner = '';
        parsedLines.forEach((parts, index) => {
          const lineY = lineYStart + (index * lineHeightMm) + fontSizeMm;
          let xPos = computeLineStartX(lineWidths[index] ?? maxWidth);
          parts.forEach(part => {
            const targetStyle = part.italic ? 'italic' : 'normal';
            const hasRealBoldForStyle = sources.some(s => (s?.style || 'normal') === targetStyle && (Number.isFinite(s?.weight) ? s.weight : 400) >= 600);
            const needsSyntheticBold = Boolean(part.bold && !hasRealBoldForStyle);
            const syntheticBoldStrokeWidth = needsSyntheticBold ? Math.min(Math.max(fontSizeMm * 0.06, 0.12), 0.6) : null;

            inner += buildTextPathsWithFont({
              font,
              text: part.text,
              fontSize: fontSizeMm,
              x: xPos,
              y: lineY,
              anchor: 'start',
              fill: gravurfarbe,
              stroke: needsSyntheticBold ? gravurfarbe : null,
              strokeWidth: syntheticBoldStrokeWidth
            });
            xPos += measureTextWidthWithFont(font, part.text, fontSizeMm);
          });
        });
        elements.push(`<g transform="${blockTransform}">${inner}</g>`);
      } else {
        let lineYStart = -(totalTextHeightMm / 2);
        const tspans = [];
        parsedLines.forEach((parts, index) => {
          const lineY = lineYStart + (index * lineHeightMm) + fontSizeMm;
          let xPos = computeLineStartX(lineWidths[index] ?? maxWidth);
          parts.forEach(part => {
            const fontWeight = part.bold ? 'bold' : 'normal';
            const fontStyle = part.italic ? 'italic' : 'normal';
            const fontFamily = getFontStack(config.font);
            const text = escapeXml(part.text);
            const fontString = `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontFamily}`;
            measureCtx.font = fontString;
            const measuredPx = measureCtx.measureText(part.text).width;
            const measuredMm = measuredPx * pxToMm;
            tspans.push(`<tspan x="${xPos}" y="${lineY}" font-family="${fontFamily}" font-size="${fontSizeMm}" font-weight="${fontWeight}" font-style="${fontStyle}" fill="${gravurfarbe}">${text}</tspan>`);
            xPos += measuredMm;
          });
        });
        elements.push(`<text transform="${blockTransform}" fill="${gravurfarbe}" text-anchor="start">${tspans.join('')}</text>`);
      }
    }

    const outline = `<rect x="0" y="0" width="${widthMM}" height="${heightMM}" fill="none" stroke="rgba(0,0,0,0.2)" stroke-width="${0.1}" />`;
    const defs = fontFaceCss ? `<defs><style><![CDATA[${fontFaceCss}]]></style></defs>` : '';
    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${widthMM}mm" height="${heightMM}mm" viewBox="0 0 ${widthMM} ${heightMM}">${defs}${outline}${elements.join('')}</svg>`;
  };

  const buildLaserSvgFromPng = (dataUrl) => {
    if (!dataUrl || !selectedRohling) return null;
    const widthMM = selectedRohling.breite || 30;
    const heightMM = selectedRohling.hoehe || 40;
    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${widthMM}mm" height="${heightMM}mm" viewBox="0 0 ${widthMM * 10} ${heightMM * 10}" preserveAspectRatio="xMidYMid meet">
  <image href="${dataUrl}" width="${widthMM * 10}" height="${heightMM * 10}" preserveAspectRatio="xMidYMid meet" />
</svg>`;
  };

  // Tinted preview cache for Holz-Anhänger (list/grid)
  useEffect(() => {
    let revoked = [];
    const buildCache = async () => {
      const entries = await Promise.all(
        (holzAnhaenger || []).map(async (item) => {
          if (!item.svgPath) return [item.id, null];
          const color = (item.farben && item.farben[0]?.hexColor) || '#d1b08c';
          try {
            const url = await loadRohlingImage(item.svgPath, color);
            if (url && url.startsWith('blob:')) {
              revoked.push(url);
            }
            return [item.id, url];
          } catch {
            return [item.id, null];
          }
        })
      );
      const map = {};
      entries.forEach(([id, url]) => { map[id] = url; });
      setHolzPreviewCache(map);
    };
    buildCache();
    return () => {
      revoked.forEach(url => URL.revokeObjectURL(url));
    };
  }, [holzAnhaenger]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rohlingeData, schriftartenData, bilderData, holzData] = await Promise.all([
        getRohlinge(),
        getSchriftarten(),
        getBilder(),
        getHolzAnhaenger()
      ]);
      const normalizedRohlinge = normalizeRohlinge(rohlingeData);
      const normalizedHolz = normalizeHolz(holzData);
      setRohlinge(normalizedRohlinge.filter(r => r.active !== false));
      setSchriftarten(Array.isArray(schriftartenData) ? schriftartenData : []);
      setBilder(Array.isArray(bilderData) ? bilderData : []);
      setHolzAnhaenger(normalizedHolz);

      if (Array.isArray(schriftartenData) && schriftartenData.length > 0) {
        const defaultFont = schriftartenData[0].name;
        setTextConfig(prev => ({
          front: prev.front.map(text => ({ ...text, font: defaultFont })),
          back: prev.back.map(text => ({ ...text, font: defaultFont }))
        }));
        setHolzFont(prev => prev || defaultFont);
      }
    } catch (error) {
      console.error('[UserView] Error loading data:', error);
      showMessage('error', 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const hasUserInput = () => {
    // Prüfe ob Texte eingegeben wurden
    const hasText = textConfig.front.some(t => t.text.trim() !== '') ||
                    textConfig.back.some(t => t.text.trim() !== '');

    // Prüfe ob Bilder hochgeladen wurden
    const hasImages = images.front.length > 0 || images.back.length > 0;

    // Prüfe ob Holz-Name eingegeben wurde
    const hasHolzInput = holzName.trim() !== '';

    return hasText || hasImages || hasHolzInput;
  };

  const handleBackToMenu = () => {
    if (hasUserInput()) {
      if (window.confirm('Möchten Sie wirklich zum Hauptmenü zurückkehren? Alle eingegebenen Daten gehen verloren.')) {
        resetToMenu();
      }
    } else {
      resetToMenu();
    }
  };

  const resetToMenu = () => {
    setActiveMenu(null);
    setSelectedRohling(null);
    setSelectedFarbvariante(null);
    setSelectedHolz(null);
    setSelectedHolzSvgIndex(0);
    setHolzName('');
    setHolzTextTransform({ x: 0, y: 0, scale: 1 });
    setHolzTextSelected(false);
    setHolzTextInteraction({ dragging: false, resizing: false, handle: null });
    setTextConfig({
      front: [
        { id: '1', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '2', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '3', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
      ],
      back: [
        { id: '1', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '2', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '3', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
      ]
    });
    setImages({ front: [], back: [] });
  };

  // Event-Listener für Header-Klick
  useEffect(() => {
    const handleHomeClick = (e) => {
      if (activeMenu) {
        e.preventDefault();
        handleBackToMenu();
      }
    };

    window.addEventListener('homeNavigation', handleHomeClick);
    return () => window.removeEventListener('homeNavigation', handleHomeClick);
  }, [activeMenu, textConfig, images, holzName]);

  const handleHolzSelect = (item) => {
    setSelectedHolz(item);
    setSelectedHolzColor(item.farben?.[0]?.id || null);
    setSelectedHolzSvgIndex(0);
    setHolzName('');
    setHolzTextTransform({ x: 0, y: 0, scale: 1 });
    setHolzTextSelected(false);
    setHolzTextInteraction({ dragging: false, resizing: false, handle: null });
    if (schriftarten.length > 0) {
      setHolzFont(schriftarten[0].name);
    }
  };

  const resetHolzSelection = () => {
    setSelectedHolz(null);
    setSelectedHolzKategorie(null);
    setSelectedHolzColor(null);
    setSelectedHolzSvgIndex(0);
    setHolzName('');
    setHolzTextTransform({ x: 0, y: 0, scale: 1 });
    setHolzTextSelected(false);
    setHolzTextInteraction({ dragging: false, resizing: false, handle: null });
  };

  const handleRohlingSelect = (rohling) => {
    setSelectedRohling(rohling);
    // Wähle die erste Farbvariante automatisch aus
    if (rohling.farbvarianten && rohling.farbvarianten.length > 0) {
      setSelectedFarbvariante(rohling.farbvarianten[0]);
    }
    const defaultFont = schriftarten[0]?.name || 'Arial';
    setTextConfig({
      front: [
        { id: '1', text: '', font: defaultFont, size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '2', text: '', font: defaultFont, size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '3', text: '', font: defaultFont, size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
      ],
      back: [
        { id: '1', text: '', font: defaultFont, size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '2', text: '', font: defaultFont, size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
        { id: '3', text: '', font: defaultFont, size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
      ]
    });
    setSelectedTextIndex({ front: null, back: null });
  };

  const handleColorChange = (farbvariante) => {
    // Farbvariante wechseln ohne Texteingaben zu l?schen
    setSelectedFarbvariante(farbvariante);
  };

  const renderVariantPreview = (farbvariante) => {
    const mode = farbvariante.mode || (farbvariante.frontImage || farbvariante.backImage ? 'image' : 'color');
    const maskUrl = selectedRohling?.masterSvg ? buildAssetUrl(selectedRohling.masterSvg) : null;
    const color = farbvariante.farbeHex || '#cccccc';

    if (mode === 'image' && farbvariante.frontImage) {
      return (
        <img
          src={buildAssetUrl(farbvariante.frontImage)}
          alt={farbvariante.farbe}
        />
      );
    }

    if (maskUrl) {
      return (
        <div
          className="color-svg-thumb"
          style={{
            backgroundColor: color,
            WebkitMaskImage: `url(${maskUrl})`,
            maskImage: `url(${maskUrl})`,
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain'
          }}
          aria-label={farbvariante.farbe}
        />
      );
    }

    return (
      <span
        className="color-dot"
        style={{ backgroundColor: color }}
        aria-label={farbvariante.farbe}
      />
    );
  };

  const handleTextConfigChange = (side, textIndex, field, value) => {
    setTextConfig(prev => {
      const updates = { [field]: value };

      // Reset position when alignment changes
      if (field === 'alignHorizontal' || field === 'alignVertical') {
        updates.x = 0;
        updates.y = 0;
      }

      return {
        ...prev,
        [side]: prev[side].map((text, idx) =>
          idx === textIndex ? { ...text, ...updates } : text
        )
      };
    });
  };

  const showNextTextField = (side) => {
    setVisibleTextFields(prev => {
      const next = [...prev[side]];
      const nextIndex = next.findIndex((visible, idx) => idx > 0 && !visible);
      if (nextIndex === -1) return prev;
      next[nextIndex] = true;
      return { ...prev, [side]: next };
    });
  };

  const resetTextFieldConfig = (side, textIndex) => {
    setTextConfig(prev => {
      const next = [...prev[side]];
      next[textIndex] = createEmptyTextConfig(String(textIndex + 1));
      return { ...prev, [side]: next };
    });
    setSelectedTextIndex(prev => {
      if (prev[side] === textIndex) {
        return { ...prev, [side]: null };
      }
      return prev;
    });
  };

  const hideTextField = (side, textIndex) => {
    if (textIndex === 0) return;
    setVisibleTextFields(prev => {
      const next = [...prev[side]];
      next[textIndex] = false;
      return { ...prev, [side]: next };
    });
    resetTextFieldConfig(side, textIndex);
  };

  // Hilfsfunktionen für Bildverwaltung
  const addImage = (side, bildId) => {
    const newImage = {
      id: `img-${Date.now()}-${Math.random()}`,
      bildId: bildId,
      scale: 0.3,
      x: 50,
      y: 50,
      rotation: 0
    };
    setImages(prev => ({
      ...prev,
      [side]: [...prev[side], newImage]
    }));
    setSelectedImageId(prev => ({
      ...prev,
      [side]: newImage.id
    }));
  };

  const removeImage = (side, imageId) => {
    setImages(prev => ({
      ...prev,
      [side]: prev[side].filter(img => img.id !== imageId)
    }));
    if (selectedImageId[side] === imageId) {
      setSelectedImageId(prev => ({
        ...prev,
        [side]: null
      }));
    }
  };

  const updateImage = (side, imageId, updates) => {
    setImages(prev => ({
      ...prev,
      [side]: prev[side].map(img =>
        img.id === imageId ? { ...img, ...updates } : img
      )
    }));
  };

  const getSelectedImage = (side) => {
    const imgId = selectedImageId[side];
    return images[side].find(img => img.id === imgId);
  };

  // Hilfsfunktion zum Konvertieren von FontAwesome-Icons in ein Bild
  // FontAwesome-Icon Cache, um wiederholtes Rendern zu sparen
  const faImageCache = React.useRef(new Map());
  const fontAwesomeToImage = (iconName, iconStyle, size = 512) => {
    const cacheKey = `${iconStyle}-${iconName}-${size}`;
    if (faImageCache.current.has(cacheKey)) {
      return Promise.resolve(faImageCache.current.get(cacheKey).cloneNode());
    }
    return new Promise((resolve, reject) => {
      try {
        const prefix = iconStyle === 'brands' ? 'fab' : iconStyle === 'regular' ? 'far' : 'fas';
        const iconObj = icon({ prefix, iconName });

        if (!iconObj) {
          reject(new Error(`Icon ${iconName} nicht gefunden`));
          return;
        }

        const svgNS = "http://www.w3.org/2000/svg";
        const svg = document.createElementNS(svgNS, "svg");
        svg.setAttribute("viewBox", `0 0 ${iconObj.icon[0]} ${iconObj.icon[1]}`);
        svg.setAttribute("width", size.toString());
        svg.setAttribute("height", size.toString());

        const pathEl = document.createElementNS(svgNS, "path");
        pathEl.setAttribute("d", iconObj.icon[4]);
        pathEl.setAttribute("fill", "currentColor");
        svg.appendChild(pathEl);

        const svgString = new XMLSerializer().serializeToString(svg);
        const svgBase64 = btoa(unescape(encodeURIComponent(svgString)));

        const img = new Image();
        img.onload = () => {
          faImageCache.current.set(cacheKey, img);
          resolve(img.cloneNode());
        };
        img.onerror = () => reject(new Error('Fehler beim Laden des Icons'));
        img.src = `data:image/svg+xml;base64,${svgBase64}`;
      } catch (error) {
        reject(error);
      }
    });
  };

  // Hilfsfunktion: Prüfe ob Punkt in Bild liegt
  const isPointInImage = (x, y, img, bild) => {
    const width = 512 * img.scale;
    const height = 512 * img.scale;
    return x >= img.x && x <= img.x + width && y >= img.y && y <= img.y + height;
  };

  // Hilfsfunktion: Prüfe ob Punkt in Handle liegt
  const getHandleAtPoint = (x, y, img) => {
    const width = 512 * img.scale;
    const height = 512 * img.scale;
    const handleSize = 20;

    // Resize-Handles (Ecken)
    const handles = [
      { type: 'resize-tl', x: img.x, y: img.y },
      { type: 'resize-tr', x: img.x + width, y: img.y },
      { type: 'resize-bl', x: img.x, y: img.y + height },
      { type: 'resize-br', x: img.x + width, y: img.y + height },
      // Rotations-Handle (oben mittig, etwas darüber)
      { type: 'rotate', x: img.x + width / 2, y: img.y - 30 }
    ];

    for (const handle of handles) {
      if (Math.abs(x - handle.x) < handleSize / 2 && Math.abs(y - handle.y) < handleSize / 2) {
        return handle.type;
      }
    }
    return null;
  };

  // Mouse-Down-Handler
  const handleImageMouseDown = (side, e) => {
    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    if (!canvas) return false;

    const rect = canvas.getBoundingClientRect();
    // Gravurbereich = gesamtes Canvas
    const gravurbereich = {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height
    };

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    // Prüfe ob Delete-Button des ausgewählten Bildes getroffen wurde
    if (selectedImageId[side]) {
      const selectedImg = getSelectedImage(side);
      if (selectedImg) {
        const imgWidth = 512 * selectedImg.scale;
        const boxX = gravurbereich.x + selectedImg.x;
        const boxY = gravurbereich.y + selectedImg.y;
        const deleteX = boxX + imgWidth + 5;
        const deleteY = boxY - 5;
        const dist = Math.sqrt(Math.pow(canvasX - deleteX, 2) + Math.pow(canvasY - deleteY, 2));

        if (dist < 15) {
          // Delete-Button getroffen
          removeImage(side, selectedImg.id);
          return true;
        }
      }
    }

    const relX = canvasX - gravurbereich.x;
    const relY = canvasY - gravurbereich.y;

    // Prüfe ob ein Handle des ausgewählten Bildes getroffen wurde
    if (selectedImageId[side]) {
      const selectedImg = getSelectedImage(side);
      if (selectedImg) {
        const handle = getHandleAtPoint(relX, relY, selectedImg);
        if (handle) {
          setDragStart(prev => ({
            ...prev,
            [side]: {
              handleType: handle,
              x: relX,
              y: relY,
              initialImg: { ...selectedImg }
            }
          }));
          setDraggingImage(prev => ({ ...prev, [side]: true }));
          return true;
        }
      }
    }

    // Prüfe ob ein Bild getroffen wurde (von oben nach unten)
    for (let i = images[side].length - 1; i >= 0; i--) {
      const img = images[side][i];
      const bild = bilder.find(b => b.id === img.bildId);
      if (bild && isPointInImage(relX, relY, img, bild)) {
        setSelectedImageId(prev => ({ ...prev, [side]: img.id }));
        setDragStart(prev => ({
          ...prev,
          [side]: {
            handleType: 'move',
            x: relX - img.x,
            y: relY - img.y
          }
        }));
        setDraggingImage(prev => ({ ...prev, [side]: true }));
        return true;
      }
    }

    // Kein Bild getroffen - Auswahl aufheben
    setSelectedImageId(prev => ({ ...prev, [side]: null }));
    return false;
  };

  // Mouse-Move-Handler für Drag & Drop (auf Haupt-Canvas)
  const handleImageMouseMove = (side, e) => {
    if (!draggingImage[side]) return;
    if (!selectedImageId[side]) return;

    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Gravurbereich = gesamtes Canvas
    const gravurbereich = {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height
    };

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    const relX = canvasX - gravurbereich.x;
    const relY = canvasY - gravurbereich.y;

    const handleType = dragStart[side].handleType;
    const selectedImg = getSelectedImage(side);
    if (!selectedImg) return;

    if (handleType === 'move') {
      // Bild verschieben
      updateImage(side, selectedImg.id, {
        x: relX - dragStart[side].x,
        y: relY - dragStart[side].y
      });
    } else if (handleType.startsWith('resize-')) {
      // Skalierung von Ecke aus
      const initialImg = dragStart[side].initialImg;
      const imgWidth = 512 * initialImg.scale;
      const imgHeight = 512 * initialImg.scale;

      let newScale;
      if (handleType === 'resize-br') {
        // Rechts-unten: Abstand vom Start-Punkt
        const dx = relX - initialImg.x;
        const dy = relY - initialImg.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
        newScale = (dist / initialDist) * initialImg.scale;
      } else if (handleType === 'resize-tr') {
        // Rechts-oben
        const dx = relX - initialImg.x;
        const dy = (initialImg.y + imgHeight) - relY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
        newScale = (dist / initialDist) * initialImg.scale;
      } else if (handleType === 'resize-bl') {
        // Links-unten
        const dx = (initialImg.x + imgWidth) - relX;
        const dy = relY - initialImg.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
        newScale = (dist / initialDist) * initialImg.scale;
      } else if (handleType === 'resize-tl') {
        // Links-oben
        const dx = (initialImg.x + imgWidth) - relX;
        const dy = (initialImg.y + imgHeight) - relY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
        newScale = (dist / initialDist) * initialImg.scale;
      }

      newScale = Math.max(0.1, Math.min(5, newScale));
      updateImage(side, selectedImg.id, { scale: newScale });
    } else if (handleType === 'rotate') {
      // Rotation um Bildmitte
      const imgWidth = 512 * selectedImg.scale;
      const imgHeight = 512 * selectedImg.scale;
      const centerX = selectedImg.x + imgWidth / 2;
      const centerY = selectedImg.y + imgHeight / 2;

      const angle = Math.atan2(relY - centerY, relX - centerX);
      const startAngle = Math.atan2(
        dragStart[side].y - centerY,
        dragStart[side].x - centerX
      );

      const rotationChange = (angle - startAngle) * (180 / Math.PI);
      const newRotation = (dragStart[side].initialImg.rotation + rotationChange) % 360;

      updateImage(side, selectedImg.id, { rotation: newRotation });
    }
  };

  // Mouse-Up-Handler für Drag & Drop
  const handleImageMouseUp = (side) => {
    setDraggingImage(prev => ({ ...prev, [side]: false }));
  };

  // ==== TEXT INTERACTION HANDLERS ====

  // Helper: Berechne Text-Position und -Dimensionen
  const getTextBounds = (side, textIndex) => {
    if (textIndex === null || textIndex === undefined) return null;
    const config = textConfig[side][textIndex];
    if (!config || !config.text || !selectedRohling) return null;

    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    const ctx = canvas.getContext('2d');
    // Gravurbereich = gesamtes Canvas
    const gravurbereich = {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height
    };

    const lines = config.text.split('\n');
    const fontSize = config.size * config.scale;
    const lineHeight = fontSize * 1.2;
    const totalTextHeight = lines.length * lineHeight;

    // Berechne Breite
    let maxWidth = 0;
    lines.forEach(line => {
      const parts = parseFormattedText(line, config);
      let lineWidth = 0;
      parts.forEach(part => {
        const fontFamily = getFontStack(config.font);
        const fontStyle = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
        ctx.font = fontStyle;
        lineWidth += ctx.measureText(part.text).width;
      });
      maxWidth = Math.max(maxWidth, lineWidth);
    });

    // Berechne Basisposition basierend auf Alignment
    let baseX = gravurbereich.x;
    let baseY = gravurbereich.y;

    if (config.alignVertical === 'center') {
      baseY = gravurbereich.y + (gravurbereich.height - totalTextHeight) / 2;
    } else if (config.alignVertical === 'bottom') {
      baseY = gravurbereich.y + gravurbereich.height - totalTextHeight;
    }

    if (config.alignHorizontal === 'center') {
      baseX = gravurbereich.x + (gravurbereich.width - maxWidth) / 2;
    } else if (config.alignHorizontal === 'right') {
      baseX = gravurbereich.x + gravurbereich.width - maxWidth;
    }

    const mmToPixel = canvas.width / selectedRohling.breite;
    const centerX = baseX + maxWidth / 2 + config.x * mmToPixel;
    const centerY = baseY + totalTextHeight / 2 + config.y * mmToPixel;

    return { centerX, centerY, width: maxWidth, height: totalTextHeight };
  };

  // Helper: Prüfe ob Punkt in Text-Handle ist
  const getTextHandleAtPoint = (side, textIndex, canvasX, canvasY) => {
    const bounds = getTextBounds(side, textIndex);
    if (!bounds) return null;

    const config = textConfig[side][textIndex];
    const rotation = (config.rotation * Math.PI) / 180;
    const cos = Math.cos(-rotation);
    const sin = Math.sin(-rotation);

    // Transform point to text's local space
    const dx = canvasX - bounds.centerX;
    const dy = canvasY - bounds.centerY;
    const localX = dx * cos - dy * sin;
    const localY = dx * sin + dy * cos;

    const halfW = bounds.width / 2;
    const halfH = bounds.height / 2;
    const handleSize = 12;
    const tolerance = handleSize;

    // Check rotation handle
    const rotateHandleY = -halfH - 30;
    if (Math.abs(localX - halfW) < tolerance && Math.abs(localY - rotateHandleY) < tolerance) {
      return 'rotate';
    }

    // Check corner handles (nur Top-Right für Skalierung)
    const corners = [
      { handle: 'resize-tr', x: halfW, y: -halfH }
    ];

    for (const corner of corners) {
      if (Math.abs(localX - corner.x) < tolerance && Math.abs(localY - corner.y) < tolerance) {
        return corner.handle;
      }
    }

    // Check if in text body
    if (Math.abs(localX) < halfW && Math.abs(localY) < halfH) {
      return 'move';
    }

    return null;
  };

  // Text Mouse Down Handler
  const handleTextMouseDown = (side, e) => {
    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    // Loop through all texts (in reverse order to handle top-most first)
    for (let textIndex = textConfig[side].length - 1; textIndex >= 0; textIndex--) {
      const config = textConfig[side][textIndex];
      if (!config.text || config.text.trim() === '') continue;

      const handle = getTextHandleAtPoint(side, textIndex, canvasX, canvasY);
      if (!handle) continue;

      // Found a text that was clicked
      setSelectedTextIndex(prev => ({
        ...prev,
        [side]: textIndex
      }));

      setTextInteraction(prev => ({
        ...prev,
        [side]: {
          dragging: handle === 'move',
          resizing: handle.startsWith('resize-'),
          rotating: handle === 'rotate',
          handle
        }
      }));

      setTextDragStart(prev => ({
        ...prev,
        [side]: {
          x: canvasX,
          y: canvasY,
          textIndex: textIndex,
          initialConfig: { ...config }
        }
      }));

      return true;
    }

    return false;
  };

  // Text Mouse Move Handler
  const handleTextMouseMove = (side, e) => {
    const interaction = textInteraction[side];
    if (!interaction.dragging && !interaction.resizing && !interaction.rotating) return;

    const textIndex = textDragStart[side].textIndex;
    if (textIndex === undefined || textIndex === null) return;

    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    const mmToPixel = canvas.width / selectedRohling.breite;
    const pixelToMm = 1 / mmToPixel;

    if (interaction.dragging) {
      // Move text
      const dx = (canvasX - textDragStart[side].x) * pixelToMm;
      const dy = (canvasY - textDragStart[side].y) * pixelToMm;

      setTextConfig(prev => {
        const newTextArray = [...prev[side]];
        newTextArray[textIndex] = {
          ...newTextArray[textIndex],
          x: textDragStart[side].initialConfig.x + dx,
          y: textDragStart[side].initialConfig.y + dy
        };
        return {
          ...prev,
          [side]: newTextArray
        };
      });
    } else if (interaction.resizing) {
      // Resize text (scale)
      const bounds = getTextBounds(side, textIndex);
      if (!bounds) return;

      const initialConfig = textDragStart[side].initialConfig;
      const startDist = Math.sqrt(
        Math.pow(textDragStart[side].x - bounds.centerX, 2) +
        Math.pow(textDragStart[side].y - bounds.centerY, 2)
      );
      const currentDist = Math.sqrt(
        Math.pow(canvasX - bounds.centerX, 2) +
        Math.pow(canvasY - bounds.centerY, 2)
      );

      const scaleFactor = currentDist / startDist;
      const newScale = Math.max(0.1, Math.min(5, initialConfig.scale * scaleFactor));

      setTextConfig(prev => {
        const newTextArray = [...prev[side]];
        newTextArray[textIndex] = {
          ...newTextArray[textIndex],
          scale: newScale
        };
        return {
          ...prev,
          [side]: newTextArray
        };
      });
    } else if (interaction.rotating) {
      // Rotate text
      const bounds = getTextBounds(side, textIndex);
      if (!bounds) return;

      const startAngle = Math.atan2(
        textDragStart[side].y - bounds.centerY,
        textDragStart[side].x - bounds.centerX
      );
      const currentAngle = Math.atan2(
        canvasY - bounds.centerY,
        canvasX - bounds.centerX
      );

      const rotationChange = (currentAngle - startAngle) * (180 / Math.PI);
      const newRotation = (textDragStart[side].initialConfig.rotation + rotationChange) % 360;

      setTextConfig(prev => {
        const newTextArray = [...prev[side]];
        newTextArray[textIndex] = {
          ...newTextArray[textIndex],
          rotation: newRotation
        };
        return {
          ...prev,
          [side]: newTextArray
        };
      });
    }
  };

  // Text Mouse Up Handler
  const handleTextMouseUp = (side) => {
    setTextInteraction(prev => ({
      ...prev,
      [side]: { dragging: false, resizing: false, rotating: false, handle: null }
    }));
  };

  // ==== TEXT TOUCH HANDLERS ====

  const handleTextTouchStart = (side, e) => {
    if (e.touches.length === 0) return false;

    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const touch = e.touches[0];
    const canvasX = (touch.clientX - rect.left) * scaleX;
    const canvasY = (touch.clientY - rect.top) * scaleY;

    // Loop through all texts (in reverse order to handle top-most first)
    for (let textIndex = textConfig[side].length - 1; textIndex >= 0; textIndex--) {
      const config = textConfig[side][textIndex];
      if (!config.text || config.text.trim() === '') continue;

      const handle = getTextHandleAtPoint(side, textIndex, canvasX, canvasY);
      if (!handle) continue;

      // Found a text that was touched
      setSelectedTextIndex(prev => ({
        ...prev,
        [side]: textIndex
      }));

      setTextInteraction(prev => ({
        ...prev,
        [side]: {
          dragging: handle === 'move',
          resizing: handle.startsWith('resize-'),
          rotating: handle === 'rotate',
          handle
        }
      }));

      setTextDragStart(prev => ({
        ...prev,
        [side]: {
          x: canvasX,
          y: canvasY,
          textIndex: textIndex,
          initialConfig: { ...config }
        }
      }));

      return true;
    }

    return false;
  };

  const handleTextTouchMove = (side, e) => {
    const interaction = textInteraction[side];
    if ((!interaction.dragging && !interaction.resizing && !interaction.rotating) || e.touches.length === 0) return;

    const textIndex = textDragStart[side].textIndex;
    if (textIndex === undefined || textIndex === null) return;

    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const touch = e.touches[0];
    const canvasX = (touch.clientX - rect.left) * scaleX;
    const canvasY = (touch.clientY - rect.top) * scaleY;

    const mmToPixel = canvas.width / selectedRohling.breite;
    const pixelToMm = 1 / mmToPixel;

    if (interaction.dragging) {
      const dx = (canvasX - textDragStart[side].x) * pixelToMm;
      const dy = (canvasY - textDragStart[side].y) * pixelToMm;

      setTextConfig(prev => {
        const newTextArray = [...prev[side]];
        newTextArray[textIndex] = {
          ...newTextArray[textIndex],
          x: textDragStart[side].initialConfig.x + dx,
          y: textDragStart[side].initialConfig.y + dy
        };
        return {
          ...prev,
          [side]: newTextArray
        };
      });
    } else if (interaction.resizing) {
      const bounds = getTextBounds(side, textIndex);
      if (!bounds) return;

      const initialConfig = textDragStart[side].initialConfig;
      const startDist = Math.sqrt(
        Math.pow(textDragStart[side].x - bounds.centerX, 2) +
        Math.pow(textDragStart[side].y - bounds.centerY, 2)
      );
      const currentDist = Math.sqrt(
        Math.pow(canvasX - bounds.centerX, 2) +
        Math.pow(canvasY - bounds.centerY, 2)
      );

      const scaleFactor = currentDist / startDist;
      const newScale = Math.max(0.1, Math.min(5, initialConfig.scale * scaleFactor));

      setTextConfig(prev => {
        const newTextArray = [...prev[side]];
        newTextArray[textIndex] = {
          ...newTextArray[textIndex],
          scale: newScale
        };
        return {
          ...prev,
          [side]: newTextArray
        };
      });
    } else if (interaction.rotating) {
      const bounds = getTextBounds(side, textIndex);
      if (!bounds) return;

      const startAngle = Math.atan2(
        textDragStart[side].y - bounds.centerY,
        textDragStart[side].x - bounds.centerX
      );
      const currentAngle = Math.atan2(
        canvasY - bounds.centerY,
        canvasX - bounds.centerX
      );

      const rotationChange = (currentAngle - startAngle) * (180 / Math.PI);
      const newRotation = (textDragStart[side].initialConfig.rotation + rotationChange) % 360;

      setTextConfig(prev => {
        const newTextArray = [...prev[side]];
        newTextArray[textIndex] = {
          ...newTextArray[textIndex],
          rotation: newRotation
        };
        return {
          ...prev,
          [side]: newTextArray
        };
      });
    }
  };

  const handleTextTouchEnd = (side) => {
    setTextInteraction(prev => ({
      ...prev,
      [side]: { dragging: false, resizing: false, rotating: false, handle: null }
    }));
  };

  // Combined handlers that check text first, then images
  const handleCanvasMouseDown = (side, e) => {
    // Try text first
    const textHandled = handleTextMouseDown(side, e);
    if (textHandled) return;

    // Then try images
    const imageHandled = handleImageMouseDown(side, e);

    // If neither text nor image was clicked, deselect text
    if (!textHandled && !imageHandled) {
      setSelectedTextIndex(prev => ({
        ...prev,
        [side]: null
      }));
    }
  };

  const handleCanvasMouseMove = (side, e) => {
    // Handle text interaction
    handleTextMouseMove(side, e);

    // Handle image interaction
    handleImageMouseMove(side, e);
  };

  const handleCanvasMouseUp = (side) => {
    handleTextMouseUp(side);
    handleImageMouseUp(side);
  };

  const handleCanvasTouchStart = (side, e) => {
    // Try text first
    const textHandled = handleTextTouchStart(side, e);
    if (textHandled) {
      e.preventDefault();
      return;
    }

    // Then try images
    const imageHandled = handleImageTouchStart(side, e);

    // If neither text nor image was touched, deselect text
    if (!textHandled && !imageHandled) {
      setSelectedTextIndex(prev => ({
        ...prev,
        [side]: null
      }));
    }
  };

  const handleCanvasTouchMove = (side, e) => {
    handleTextTouchMove(side, e);
    handleImageTouchMove(side, e);
  };

  const handleCanvasTouchEnd = (side) => {
    handleTextTouchEnd(side);
    handleImageTouchEnd(side);
  };

  // Touch-Event-Handler für mobile Geräte
  const handleImageTouchStart = (side, e) => {
    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    if (!canvas) return false;

    const rect = canvas.getBoundingClientRect();
    // Gravurbereich = gesamtes Canvas
    const gravurbereich = {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height
    };

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches.length === 1) {
      // Einzelner Touch: Auswahl oder Verschieben
      const touch = e.touches[0];
      const canvasX = (touch.clientX - rect.left) * scaleX;
      const canvasY = (touch.clientY - rect.top) * scaleY;
      const relX = canvasX - gravurbereich.x;
      const relY = canvasY - gravurbereich.y;

      // Prüfe ob ein Handle des ausgewählten Bildes getroffen wurde
      if (selectedImageId[side]) {
        const selectedImg = getSelectedImage(side);
        if (selectedImg) {
          const handle = getHandleAtPoint(relX, relY, selectedImg);
          if (handle) {
            setDragStart(prev => ({
              ...prev,
              [side]: {
                handleType: handle,
                x: relX,
                y: relY,
                initialImg: { ...selectedImg },
                touchMode: 'single'
              }
            }));
            setDraggingImage(prev => ({ ...prev, [side]: true }));
            return true;
          }
        }
      }

      // Prüfe ob ein Bild getroffen wurde
      for (let i = images[side].length - 1; i >= 0; i--) {
        const img = images[side][i];
        const bild = bilder.find(b => b.id === img.bildId);
        if (bild && isPointInImage(relX, relY, img, bild)) {
          setSelectedImageId(prev => ({ ...prev, [side]: img.id }));
          setDragStart(prev => ({
            ...prev,
            [side]: {
              handleType: 'move',
              x: relX - img.x,
              y: relY - img.y,
              touchMode: 'single'
            }
          }));
          setDraggingImage(prev => ({ ...prev, [side]: true }));
          return true;
        }
      }

      // Kein Bild getroffen
      setSelectedImageId(prev => ({ ...prev, [side]: null }));
      return false;
    } else if (e.touches.length === 2 && selectedImageId[side]) {
      // Zwei Finger: Pinch-to-Zoom und Rotation
      const selectedImg = getSelectedImage(side);
      if (!selectedImg) return false;

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      const x1 = (touch1.clientX - rect.left) * scaleX - gravurbereich.x;
      const y1 = (touch1.clientY - rect.top) * scaleY - gravurbereich.y;
      const x2 = (touch2.clientX - rect.left) * scaleX - gravurbereich.x;
      const y2 = (touch2.clientY - rect.top) * scaleY - gravurbereich.y;

      const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
      const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);

      setDragStart(prev => ({
        ...prev,
        [side]: {
          touchMode: 'multi',
          initialDistance: distance,
          initialAngle: angle,
          initialImg: { ...selectedImg }
        }
      }));
      setDraggingImage(prev => ({ ...prev, [side]: true }));
      return true;
    }

    return false;
  };

  const handleImageTouchMove = (side, e) => {
    if (!draggingImage[side]) return;
    if (!dragStart[side]) return;

    e.preventDefault();

    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    // Gravurbereich = gesamtes Canvas
    const gravurbereich = {
      x: 0,
      y: 0,
      width: canvas.width,
      height: canvas.height
    };

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches.length === 1 && dragStart[side].touchMode === 'single') {
      // Einzelner Touch: Verschieben oder Handle
      const touch = e.touches[0];
      const canvasX = (touch.clientX - rect.left) * scaleX;
      const canvasY = (touch.clientY - rect.top) * scaleY;
      const relX = canvasX - gravurbereich.x;
      const relY = canvasY - gravurbereich.y;

      const selectedImg = getSelectedImage(side);
      if (!selectedImg) return;

      const handleType = dragStart[side].handleType;

      if (handleType === 'move') {
        updateImage(side, selectedImg.id, {
          x: relX - dragStart[side].x,
          y: relY - dragStart[side].y
        });
      } else if (handleType.startsWith('resize-')) {
        // Resize-Logik (wie bei Maus)
        const initialImg = dragStart[side].initialImg;
        const imgWidth = 512 * initialImg.scale;
        const imgHeight = 512 * initialImg.scale;

        let newScale;
        if (handleType === 'resize-br') {
          const dx = relX - initialImg.x;
          const dy = relY - initialImg.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
          newScale = (dist / initialDist) * initialImg.scale;
        } else if (handleType === 'resize-tr') {
          const dx = relX - initialImg.x;
          const dy = (initialImg.y + imgHeight) - relY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
          newScale = (dist / initialDist) * initialImg.scale;
        } else if (handleType === 'resize-bl') {
          const dx = (initialImg.x + imgWidth) - relX;
          const dy = relY - initialImg.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
          newScale = (dist / initialDist) * initialImg.scale;
        } else if (handleType === 'resize-tl') {
          const dx = (initialImg.x + imgWidth) - relX;
          const dy = (initialImg.y + imgHeight) - relY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const initialDist = Math.sqrt(imgWidth * imgWidth + imgHeight * imgHeight);
          newScale = (dist / initialDist) * initialImg.scale;
        }

        newScale = Math.max(0.1, Math.min(5, newScale));
        updateImage(side, selectedImg.id, { scale: newScale });
      } else if (handleType === 'rotate') {
        const imgWidth = 512 * selectedImg.scale;
        const imgHeight = 512 * selectedImg.scale;
        const centerX = selectedImg.x + imgWidth / 2;
        const centerY = selectedImg.y + imgHeight / 2;

        const angle = Math.atan2(relY - centerY, relX - centerX);
        const startAngle = Math.atan2(
          dragStart[side].y - centerY,
          dragStart[side].x - centerX
        );

        const rotationChange = (angle - startAngle) * (180 / Math.PI);
        const newRotation = (dragStart[side].initialImg.rotation + rotationChange) % 360;

        updateImage(side, selectedImg.id, { rotation: newRotation });
      }
    } else if (e.touches.length === 2 && dragStart[side].touchMode === 'multi') {
      // Zwei Finger: Pinch und Rotation
      const selectedImg = getSelectedImage(side);
      if (!selectedImg) return;

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      const x1 = (touch1.clientX - rect.left) * scaleX - gravurbereich.x;
      const y1 = (touch1.clientY - rect.top) * scaleY - gravurbereich.y;
      const x2 = (touch2.clientX - rect.left) * scaleX - gravurbereich.x;
      const y2 = (touch2.clientY - rect.top) * scaleY - gravurbereich.y;

      const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
      const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);

      const initialImg = dragStart[side].initialImg;

      // Skalierung basierend auf Pinch
      const scaleFactor = distance / dragStart[side].initialDistance;
      const newScale = Math.max(0.1, Math.min(5, initialImg.scale * scaleFactor));

      // Rotation basierend auf Zwei-Finger-Drehung
      const rotationChange = angle - dragStart[side].initialAngle;
      const newRotation = (initialImg.rotation + rotationChange) % 360;

      updateImage(side, selectedImg.id, {
        scale: newScale,
        rotation: newRotation
      });
    }
  };

  const handleImageTouchEnd = (side) => {
    setDraggingImage(prev => ({ ...prev, [side]: false }));
  };

  // Scroll-Handler für Skalierung (auf Haupt-Canvas)
  const handleImageWheel = (side, e) => {
    // Nur interagieren wenn ein Bild ausgewählt ist
    if (!selectedImageId[side]) return;

    e.preventDefault();
    const selectedImg = getSelectedImage(side);
    if (!selectedImg) return;

    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    const newScale = Math.max(0.1, Math.min(5, selectedImg.scale + delta));
    updateImage(side, selectedImg.id, { scale: newScale });
  };

  // Formatierung auf Text anwenden - textIndex wird explizit übergeben
  const toggleFormatting = (side, textIndex, formatType) => {
    setTextConfig(prev => ({
      ...prev,
      [side]: prev[side].map((textObj, idx) => {
        if (idx === textIndex) {
          return {
            ...textObj,
            [formatType]: !textObj[formatType]
          };
        }
        return textObj;
      })
    }));
  };

  const isFormatActive = (side, textIndex, formatType) => {
    return textConfig[side][textIndex]?.[formatType] || false;
  };

  // Hilfsfunktion: Gibt Formatierung aus Config zurück
  const parseFormattedText = (text, config) => {
    return [{
      text: text,
      bold: config.bold || false,
      italic: config.italic || false,
      underline: config.underline || false
    }];
  };

  const colorizeSvgText = (svgText, color) => {
    if (!color) return svgText;
    const fillColor = color || '#000000';
    const styleTag = `<style>path, circle, polygon, polyline, ellipse, line { fill: ${fillColor} !important; stroke: ${fillColor} !important; }</style>`;
    if (svgText.includes('<svg')) {
      return svgText.replace(/<svg([^>]*)>/, `<svg$1>${styleTag}`);
    }
    return `${styleTag}${svgText}`;
  };

  const loadRohlingImage = async (url, color) => {
    if (!url) return null;
    const assetUrl = buildAssetUrl(url);
    if (!assetUrl) return null;

    const cacheKey = `${assetUrl}|${color || 'none'}`;
    if (imageCacheRef.current.has(cacheKey)) {
      return imageCacheRef.current.get(cacheKey);
    }

    if (assetUrl.toLowerCase().endsWith('.svg')) {
      try {
        const res = await fetch(assetUrl);
        if (!res.ok) {
          throw new Error(`SVG fetch failed: ${res.status}`);
        }
        const svgText = await res.text();
        const coloredSvg = colorizeSvgText(svgText, color);
        const blobUrl = URL.createObjectURL(new Blob([coloredSvg], { type: 'image/svg+xml' }));
        imageCacheRef.current.set(cacheKey, blobUrl);
        return blobUrl;
      } catch (error) {
        console.error('SVG Färbung fehlgeschlagen, nutze Original:', error);
        imageCacheRef.current.set(cacheKey, assetUrl);
        return assetUrl;
      }
    }

    imageCacheRef.current.set(cacheKey, assetUrl);
    return assetUrl;
  };

  const renderMockup = async (side) => {
    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    if (!canvas || !selectedRohling || !selectedFarbvariante) return;

    const ctx = canvas.getContext('2d');
    const rohlingImage = new Image();
    rohlingImage.crossOrigin = 'anonymous';

    const gravurfarbeMap = {
      'gray': '#808080',
      'grey': '#808080',
      'black': '#000000'
    };
    const gravurfarbe = gravurfarbeMap[selectedRohling.gravurfarbe] || selectedRohling.gravurfarbe || '#000000';

    const variantMode = selectedFarbvariante.mode || (selectedFarbvariante.frontImage || selectedFarbvariante.backImage ? 'image' : 'color');
    const variantColor = selectedFarbvariante.farbeHex || '#cccccc';

    const rawImagePath = (() => {
      const imagePath = side === 'front'
        ? selectedFarbvariante.frontImage
        : selectedFarbvariante.backImage;
      if (variantMode === 'image' && imagePath) return imagePath;
      return selectedRohling.masterSvg || imagePath || null;
    })();

    if (!rawImagePath) {
      console.warn(`No ${side} image for selected Farbvariante`);
      return;
    }

    const imageUrl = await loadRohlingImage(rawImagePath, variantMode === 'color' ? variantColor : gravurfarbe);
    if (!imageUrl) {
      console.warn(`No image URL resolved for ${side}`);
      return;
    }

    rohlingImage.onerror = () => {
      console.error(`Failed to load image: ${imageUrl}`);
    };

    rohlingImage.onload = async () => {
      // Verwende die gleiche Pixel-Ratio wie beim Export (10 px/mm)
      const pixelPerMM = 10;
      const targetWidth = selectedRohling.breite * pixelPerMM;
      const targetHeight = selectedRohling.hoehe * pixelPerMM;

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // Rohling-Bild auf Canvas-Größe skalieren
      ctx.drawImage(rohlingImage, 0, 0, targetWidth, targetHeight);

      // Gravurbereich = gesamtes Canvas (basierend auf Bildkalibrierung)
      const gravurbereich = {
        x: 0,
        y: 0,
        width: targetWidth,
        height: targetHeight
      };

      // Konvertiere Gravurfarbe-Namen zu Hex-Werten (für Text und Bilder)
      const gravurfarbeMap = {
        'gray': '#808080',
        'grey': '#808080',
        'black': '#000000'
      };
      const gravurfarbe = gravurfarbeMap[selectedRohling.gravurfarbe] || selectedRohling.gravurfarbe || '#000000';

      // mm-Papier Raster über das gesamte Bild zeichnen (wenn aktiviert)
      if (showGrid[side]) {
        ctx.save();

        // Raster-Eigenschaften - 1mm Raster wie mm-Papier
        const gridSize = 1; // 1mm Rasterabstand
        const gridSize5 = 5; // 5mm für dickere Linien
        const gridSize10 = 10; // 10mm für noch dickere Linien

        // Berechne mm zu Pixel basierend auf Rohling-Breite
        const mmToPixel = targetWidth / selectedRohling.breite;
        const gridSpacing = gridSize * mmToPixel;
        const gridSpacing5 = gridSize5 * mmToPixel;
        const gridSpacing10 = gridSize10 * mmToPixel;

        // 1mm Raster - dünne Linien (hellgrau mit leichtem Kontrast)
        ctx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
        ctx.lineWidth = 0.5;

        for (let x = 0; x <= targetWidth; x += gridSpacing) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, targetHeight);
          ctx.stroke();
        }

        for (let y = 0; y <= targetHeight; y += gridSpacing) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(targetWidth, y);
          ctx.stroke();
        }

        // 5mm Raster - mittlere Linien
        ctx.strokeStyle = 'rgba(80, 80, 80, 0.6)';
        ctx.lineWidth = 1;

        for (let x = 0; x <= targetWidth; x += gridSpacing5) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, targetHeight);
          ctx.stroke();
        }

        for (let y = 0; y <= targetHeight; y += gridSpacing5) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(targetWidth, y);
          ctx.stroke();
        }

        // 10mm Raster - dicke Linien
        ctx.strokeStyle = 'rgba(60, 60, 80, 0.8)';
        ctx.lineWidth = 1.5;

        for (let x = 0; x <= targetWidth; x += gridSpacing10) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, targetHeight);
          ctx.stroke();
        }

        for (let y = 0; y <= targetHeight; y += gridSpacing10) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(targetWidth, y);
          ctx.stroke();
        }

        ctx.restore();
      }

      // Alle Bilder zeichnen
      const userImages = images[side];
      for (const img of userImages) {
        const bild = bilder.find(b => b.id === img.bildId);
        if (!bild) continue;

        try {
          let userImage;
          if (bild.type === 'fontawesome') {
            userImage = await fontAwesomeToImage(bild.iconName, bild.iconStyle);
          } else {
            userImage = await new Promise((resolve, reject) => {
              const imgElement = new Image();
              imgElement.crossOrigin = 'anonymous';
              imgElement.onload = () => resolve(imgElement);
              imgElement.onerror = reject;
              imgElement.src = buildAssetUrl(bild.path);
            });
          }

          // Berechne Bild-Dimensionen basierend auf Scale
          const imgWidth = 512 * img.scale;
          const imgHeight = 512 * img.scale;

          // Position innerhalb des Gravurbereichs
          const imgX = gravurbereich.x + img.x;
          const imgY = gravurbereich.y + img.y;

          // Rotation anwenden
          ctx.save();
          const centerX = imgX + imgWidth / 2;
          const centerY = imgY + imgHeight / 2;
          ctx.translate(centerX, centerY);
          ctx.rotate((img.rotation || 0) * Math.PI / 180);
          ctx.translate(-centerX, -centerY);

          // Zeichne das Bild mit Gravurfarbe eingefärbt
          // Erstelle temporären Canvas für die Einfärbung
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = imgWidth;
          tempCanvas.height = imgHeight;
          const tempCtx = tempCanvas.getContext('2d');

          // Zeichne das Bild auf den temporären Canvas
          tempCtx.drawImage(userImage, 0, 0, imgWidth, imgHeight);

          // Färbe nur die nicht-transparenten Pixel mit der Gravurfarbe ein
          tempCtx.globalCompositeOperation = 'source-in';
          tempCtx.fillStyle = gravurfarbe;
          tempCtx.fillRect(0, 0, imgWidth, imgHeight);

          // Zeichne das eingefärbte Bild auf den Haupt-Canvas
          ctx.drawImage(tempCanvas, imgX, imgY);

          ctx.restore();
        } catch (error) {
          console.error('Fehler beim Rendern des Bildes:', error);
        }
      }

      // Bounding Box für ausgewähltes Bild zeichnen
      const selectedImg = getSelectedImage(side);
      if (selectedImg) {
        const imgWidth = 512 * selectedImg.scale;
        const imgHeight = 512 * selectedImg.scale;
        const boxX = gravurbereich.x + selectedImg.x;
        const boxY = gravurbereich.y + selectedImg.y;

        // Bounding Box
        ctx.strokeStyle = '#3498db';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(boxX, boxY, imgWidth, imgHeight);
        ctx.setLineDash([]);

        // Resize-Handles (Ecken)
        const handleSize = 12;
        ctx.fillStyle = '#3498db';
        const handles = [
          { x: boxX, y: boxY }, // TL
          { x: boxX + imgWidth, y: boxY }, // TR
          { x: boxX, y: boxY + imgHeight }, // BL
          { x: boxX + imgWidth, y: boxY + imgHeight } // BR
        ];

        handles.forEach(handle => {
          ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
        });

        // Rotations-Handle (oben mittig)
        const rotateHandleX = boxX + imgWidth / 2;
        const rotateHandleY = boxY - 30;
        ctx.beginPath();
        ctx.arc(rotateHandleX, rotateHandleY, 8, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = '#2980b9';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Linie zum Rotations-Handle
        ctx.strokeStyle = '#3498db';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(boxX + imgWidth / 2, boxY);
        ctx.lineTo(rotateHandleX, rotateHandleY);
        ctx.stroke();

        // Delete-Button (oben rechts)
        const deleteX = boxX + imgWidth + 5;
        const deleteY = boxY - 5;
        ctx.fillStyle = '#e74c3c';
        ctx.beginPath();
        ctx.arc(deleteX, deleteY, 15, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('×', deleteX, deleteY);
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
      }

      // Funktion zum Zeichnen der Text Bounding Box
      const drawTextBoundingBox = (side, centerX, centerY, width, height, rotation) => {
        // This is called for the selected text only
        if (selectedTextIndex[side] === null) return;

        ctx.save();

        // Transform to text position and rotation
        ctx.translate(centerX, centerY);
        ctx.rotate((rotation * Math.PI) / 180);

        // Bounding Box
        const boxX = -width / 2;
        const boxY = -height / 2;

        ctx.strokeStyle = '#e74c3c'; // Red for text to distinguish from images
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(boxX, boxY, width, height);
        ctx.setLineDash([]);

        // Resize-Handle (nur oben rechts, semi-transparent)
        const handleSize = 12;
        ctx.fillStyle = 'rgba(231, 76, 60, 0.5)'; // Semi-transparent red
        ctx.strokeStyle = '#e74c3c';
        ctx.lineWidth = 2;
        const handleX = boxX + width;
        const handleY = boxY;

        ctx.fillRect(handleX - handleSize / 2, handleY - handleSize / 2, handleSize, handleSize);
        ctx.strokeRect(handleX - handleSize / 2, handleY - handleSize / 2, handleSize, handleSize);

        // Rotations-Handle (oben mittig)
        const rotateHandleX = width / 2;
        const rotateHandleY = -height / 2 - 30;
        ctx.beginPath();
        ctx.arc(rotateHandleX, rotateHandleY, 8, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = '#c0392b';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Linie zum Rotations-Handle
        ctx.strokeStyle = '#e74c3c';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(width / 2, -height / 2);
        ctx.lineTo(rotateHandleX, rotateHandleY);
        ctx.stroke();

        ctx.restore();
      };

      // Text zeichnen - iterate over all 3 texts
      function drawText() {
        textConfig[side].forEach((config, textIndex) => {
          if (!config.text || config.text.trim() === '') return;

          ctx.save();
          // Verwende die bereits definierte Gravurfarbe
          ctx.fillStyle = gravurfarbe;
          ctx.strokeStyle = gravurfarbe;

          const lines = config.text.split('\n');
          const fontFamily = getFontStack(config.font);
          const fontSize = config.size * config.scale;
          const lineHeight = fontSize * 1.2;
          const totalTextHeight = lines.length * lineHeight;

          // Berechne Textbreite für horizontale Ausrichtung
          let maxWidth = 0;
          lines.forEach(line => {
            const parts = parseFormattedText(line, config);
            let lineWidth = 0;
            parts.forEach(part => {
              const fontStyle = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
              ctx.font = fontStyle;
              lineWidth += ctx.measureText(part.text).width;
            });
            maxWidth = Math.max(maxWidth, lineWidth);
          });

          // Berechne Basisposition basierend auf Alignment
          let baseX = gravurbereich.x;
          let baseY = gravurbereich.y;

          if (config.alignVertical === 'center') {
            baseY = gravurbereich.y + (gravurbereich.height - totalTextHeight) / 2;
          } else if (config.alignVertical === 'bottom') {
            baseY = gravurbereich.y + gravurbereich.height - totalTextHeight;
          }

          if (config.alignHorizontal === 'center') {
            baseX = gravurbereich.x + (gravurbereich.width - maxWidth) / 2;
          } else if (config.alignHorizontal === 'right') {
            baseX = gravurbereich.x + gravurbereich.width - maxWidth;
          }

          // Füge User-Verschiebung hinzu (in mm -> pixels)
          const mmToPixel = canvas.width / selectedRohling.breite;
          const centerX = baseX + maxWidth / 2;
          const centerY = baseY + totalTextHeight / 2;
          const offsetX = config.x * mmToPixel;
          const offsetY = config.y * mmToPixel;

          // Transformationen: Translate -> Rotate
          ctx.translate(centerX + offsetX, centerY + offsetY);
          ctx.rotate((config.rotation * Math.PI) / 180);

          // Zeichne alle Zeilen
          lines.forEach((line, index) => {
            const y = -(totalTextHeight / 2) + (index * lineHeight) + fontSize;

            // Parse formatted text
            const parts = parseFormattedText(line, config);

            // Berechne Zeilenbreite
            let totalWidth = 0;
            parts.forEach(part => {
              const fontStyle = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
              ctx.font = fontStyle;
              totalWidth += ctx.measureText(part.text).width;
            });

            // Startposition für diese Zeile (zentriert relativ zum Text)
            let x = -(totalWidth / 2);

            // Zeichne jedes formatierte Segment
            parts.forEach(part => {
              const fontStyle = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
              ctx.font = fontStyle;

              // Text zeichnen
              ctx.fillText(part.text, x, y);

              // Unterstrichen
              if (part.underline) {
                const metrics = ctx.measureText(part.text);
                ctx.beginPath();
                ctx.moveTo(x, y + 2);
                ctx.lineTo(x + metrics.width, y + 2);
                ctx.lineWidth = Math.max(1, fontSize / 16);
                ctx.stroke();
              }

              // X-Position für nächstes Segment verschieben
              x += ctx.measureText(part.text).width;
            });
          });

          ctx.restore();

          // Zeichne Bounding Box und Handles wenn Text vorhanden ist (only if this text is selected)
          if (selectedTextIndex[side] === textIndex) {
            drawTextBoundingBox(side, centerX + offsetX, centerY + offsetY, maxWidth, totalTextHeight, config.rotation);
          }
        }); // end forEach
      }

      // Rufe drawText auf
      drawText();
    };

    rohlingImage.src = imageUrl;
  };

  // eslint-disable-next-line no-unused-vars
  const downloadMockup = (side) => {
    const canvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
    const link = document.createElement('a');
    link.download = `mockup-${selectedRohling.name}-${side}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  // Hilfsfunktion: Generiere Laser-Bild-Daten als Data URL
  const generateLaserImageData = async (side) => {
    if (!selectedRohling) {
      throw new Error('Kein Rohling ausgewählt');
    }

    // Erstelle einen neuen Canvas nur für die Nutzereingaben
    const exportCanvas = document.createElement('canvas');

      // Größe des Canvas EXAKT in mm (verwende tatsächliche Rohling-Maße)
      const canvasWidthMM = selectedRohling.breite || 30;
      const canvasHeightMM = selectedRohling.hoehe || 40;

      // Pixel pro mm für Export (10 pixel/mm = gute Qualität)
      const pixelPerMM = 10;

      exportCanvas.width = canvasWidthMM * pixelPerMM;
      exportCanvas.height = canvasHeightMM * pixelPerMM;

      const ctx = exportCanvas.getContext('2d');

      // Transparenter Hintergrund
      ctx.clearRect(0, 0, exportCanvas.width, exportCanvas.height);

      // Gravurfarbe
      const gravurfarbeMap = {
        'gray': '#808080',
        'grey': '#808080',
        'black': '#000000'
      };
      const gravurfarbe = gravurfarbeMap[selectedRohling.gravurfarbe] || selectedRohling.gravurfarbe || '#000000';

      // Gravurbereich = gesamter Canvas
      const gravurbereich = {
        x: 0,
        y: 0,
        width: exportCanvas.width,
        height: exportCanvas.height
      };

      // Skalierungsfaktor zwischen Mockup-Canvas und Export-Canvas
      const mockupCanvas = side === 'front' ? canvasRefFront.current : canvasRefBack.current;
      const scaleFactor = mockupCanvas ? exportCanvas.width / mockupCanvas.width : 1;

      // Debug-Ausgabe
      console.log(`[Laser Export ${side}]`, {
        mockupCanvasWidth: mockupCanvas?.width,
        exportCanvasWidth: exportCanvas.width,
        scaleFactor: scaleFactor,
        rohlingBreite: selectedRohling.breite
      });

      // mmToPixel für Export (für Text, der in mm gespeichert ist)
      const mmToPixel = exportCanvas.width / selectedRohling.breite;

      // Bilder rendern (wie im Mockup)
      for (const img of images[side]) {
        const bild = bilder.find(b => b.id === img.bildId);
        if (!bild) continue;

        try {
          let userImage;
          if (bild.type === 'fontawesome') {
            userImage = await fontAwesomeToImage(bild.iconName, bild.iconStyle);
          } else {
            userImage = await new Promise((resolve, reject) => {
              const imgElement = new Image();
              imgElement.crossOrigin = 'anonymous';
              imgElement.onload = () => resolve(imgElement);
              imgElement.onerror = reject;
              imgElement.src = buildAssetUrl(bild.path);
            });
          }

          // Berechne Bild-Dimensionen (512 ist die Standard-Icon-Größe im Mockup)
          // Verwende dieselbe Logik wie im Mockup
          const imgWidth = 512 * img.scale * scaleFactor;
          const imgHeight = 512 * img.scale * scaleFactor;

          // Position innerhalb des Gravurbereichs (skaliert auf Export-Canvas)
          const imgX = gravurbereich.x + img.x * scaleFactor;
          const imgY = gravurbereich.y + img.y * scaleFactor;

          // Debug-Ausgabe für Bildpositionierung
          console.log(`[Bild ${bild.name}]`, {
            originalX: img.x,
            originalY: img.y,
            originalScale: img.scale,
            exportX: imgX,
            exportY: imgY,
            exportWidth: imgWidth,
            exportHeight: imgHeight
          });

          // Rotation anwenden
          ctx.save();
          const centerX = imgX + imgWidth / 2;
          const centerY = imgY + imgHeight / 2;
          ctx.translate(centerX, centerY);
          ctx.rotate((img.rotation || 0) * Math.PI / 180);
          ctx.translate(-centerX, -centerY);

          // Bild mit Gravurfarbe einfärben
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = imgWidth;
          tempCanvas.height = imgHeight;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(userImage, 0, 0, imgWidth, imgHeight);
          tempCtx.globalCompositeOperation = 'source-in';
          tempCtx.fillStyle = gravurfarbe;
          tempCtx.fillRect(0, 0, imgWidth, imgHeight);

          ctx.drawImage(tempCanvas, imgX, imgY);
          ctx.restore();
        } catch (error) {
          console.error('Fehler beim Rendern des Bildes:', error);
        }
      }

      // Texte rendern (wie im Mockup)
      textConfig[side].forEach((config) => {
        if (!config.text || config.text.trim() === '') return;

        ctx.save();
        ctx.fillStyle = gravurfarbe;
        ctx.strokeStyle = gravurfarbe;

        const lines = config.text.split('\n');
        const fontFamily = getFontStack(config.font);
        const fontSize = config.size * config.scale;
        const lineHeight = fontSize * 1.2;
        const totalTextHeight = lines.length * lineHeight;

        // Berechne maximale Zeilenbreite
        let maxWidth = 0;
        lines.forEach(line => {
          const parts = parseFormattedText(line, config);
          let lineWidth = 0;
          parts.forEach(part => {
            const fontStyle = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
            ctx.font = fontStyle;
            lineWidth += ctx.measureText(part.text).width;
          });
          maxWidth = Math.max(maxWidth, lineWidth);
        });

        // Berechne Basisposition basierend auf Alignment
        let baseX = gravurbereich.x;
        let baseY = gravurbereich.y;

        if (config.alignVertical === 'center') {
          baseY = gravurbereich.y + (gravurbereich.height - totalTextHeight) / 2;
        } else if (config.alignVertical === 'bottom') {
          baseY = gravurbereich.y + gravurbereich.height - totalTextHeight;
        }

        if (config.alignHorizontal === 'center') {
          baseX = gravurbereich.x + (gravurbereich.width - maxWidth) / 2;
        } else if (config.alignHorizontal === 'right') {
          baseX = gravurbereich.x + gravurbereich.width - maxWidth;
        }

        // Füge User-Verschiebung hinzu (in mm -> pixels)
        const centerX = baseX + maxWidth / 2;
        const centerY = baseY + totalTextHeight / 2;
        const offsetX = config.x * mmToPixel;
        const offsetY = config.y * mmToPixel;

        // Transformationen: Translate -> Rotate
        ctx.translate(centerX + offsetX, centerY + offsetY);
        ctx.rotate((config.rotation * Math.PI) / 180);

        // Zeichne alle Zeilen
        lines.forEach((line, index) => {
          const y = -(totalTextHeight / 2) + (index * lineHeight) + fontSize;

          // Parse formatted text
          const parts = parseFormattedText(line, config);

          // Berechne Zeilenbreite
          let totalWidth = 0;
          parts.forEach(part => {
            const fontStyle = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
            ctx.font = fontStyle;
            totalWidth += ctx.measureText(part.text).width;
          });

          // Startposition für diese Zeile (zentriert relativ zum Text)
          let x = -(totalWidth / 2);

          // Zeichne jedes formatierte Segment
          parts.forEach(part => {
            const fontStyle = `${part.italic ? 'italic ' : ''}${part.bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
            ctx.font = fontStyle;

            // Text zeichnen
            ctx.fillText(part.text, x, y);

            // Unterstrichen
            if (part.underline) {
              const metrics = ctx.measureText(part.text);
              ctx.beginPath();
              ctx.moveTo(x, y + 2);
              ctx.lineTo(x + metrics.width, y + 2);
              ctx.lineWidth = Math.max(1, fontSize / 16);
              ctx.stroke();
            }

            // X-Position für nächstes Segment verschieben
            x += ctx.measureText(part.text).width;
          });
        });

        ctx.restore();
    });

    // Rückgabe als Data URL
    return exportCanvas.toDataURL('image/png');
  };


  const renderHolzPreview = ({ targetCanvas, forLaser } = {}) => {
    return new Promise((resolve) => {
      const canvas = targetCanvas || holzCanvasRef.current;
      if (!canvas || !selectedHolz) return resolve(null);
      const isUiCanvas = !targetCanvas || targetCanvas === holzCanvasRef.current;

      const baseWidth = selectedHolz.width || 100;
      const baseHeight = selectedHolz.height || 100;
      const targetWidth = 500;
      const scale = Math.min(targetWidth / baseWidth, targetWidth / baseHeight);
      const widthPx = baseWidth * scale;
      const heightPx = baseHeight * scale;

      canvas.width = widthPx;
      canvas.height = heightPx;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, widthPx, heightPx);

      const holzColor = getHolzColor();
      const textColor = selectedHolz.gravurfarbe || '#ff0000';

    const drawContent = (imgElement) => {
      ctx.clearRect(0, 0, widthPx, heightPx);
      const canDrawBg = imgElement && imgElement.naturalWidth > 0 && imgElement.naturalHeight > 0;
      if (canDrawBg) {
        const imgScale = Math.min(widthPx / imgElement.naturalWidth, heightPx / imgElement.naturalHeight);
        const drawW = imgElement.naturalWidth * imgScale;
        const drawH = imgElement.naturalHeight * imgScale;
        const offsetX = (widthPx - drawW) / 2;
        const offsetY = (heightPx - drawH) / 2;
        if (forLaser) {
          ctx.save();
          ctx.drawImage(imgElement, offsetX, offsetY, drawW, drawH);
          ctx.globalCompositeOperation = 'source-atop';
          ctx.fillStyle = '#ff0000';
          ctx.fillRect(offsetX, offsetY, drawW, drawH);
          ctx.restore();
        } else {
          ctx.save();
          ctx.fillStyle = holzColor;
          ctx.fillRect(offsetX, offsetY, drawW, drawH);
          ctx.globalCompositeOperation = 'destination-in';
          ctx.drawImage(imgElement, offsetX, offsetY, drawW, drawH);
          ctx.restore();
        }
      } else if (forLaser) {
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(0, 0, widthPx, heightPx);
      } else if (holzColor) {
        ctx.fillStyle = holzColor;
        ctx.fillRect(0, 0, widthPx, heightPx);
      }

      if (!holzName) return resolve(canvas);

      const textState = getHolzTextRenderState(ctx, { scalePxPerMm: scale });
      if (!textState) return resolve(canvas);

      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = forLaser ? '#ff0000' : textColor;
      ctx.font = `${textState.fontBase}${textState.fontSizePx}px ${textState.fontFamily}`;
      ctx.fillText(holzName, textState.x, textState.y);

      if (!forLaser && isUiCanvas && holzTextSelected && textState.bounds?.width) {
        ctx.save();
        ctx.strokeStyle = '#3498db';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(textState.bounds.x, textState.bounds.y, textState.bounds.width, textState.bounds.height);
        ctx.setLineDash([]);
        const handleSize = 10;
        ctx.fillStyle = '#3498db';
        ctx.fillRect(
          textState.bounds.x + textState.bounds.width - handleSize / 2,
          textState.bounds.y - handleSize / 2,
          handleSize,
          handleSize
        );
        ctx.restore();
      }
      resolve(canvas);
    };

      const imgElement = new Image();
      imgElement.crossOrigin = 'anonymous';
      imgElement.onload = () => drawContent(imgElement);
      imgElement.onerror = () => drawContent(null);
      const selectedSvgPath = getSelectedHolzSvgPath();
      if (selectedSvgPath) {
        loadRohlingImage(selectedSvgPath, forLaser ? '#ff0000' : holzColor)
          .then((url) => {
            if (url) {
              imgElement.src = url;
            } else {
              drawContent(null);
            }
          })
          .catch(() => drawContent(null));
      } else {
        drawContent(null);
      }
    });
  };

  useEffect(() => {
    if (selectedHolz) {
      renderHolzPreview().catch(() => {});
    }
  }, [selectedHolz, selectedHolzColor, selectedHolzSvgIndex, holzName, holzFont, holzStyle, holzTextTransform, holzTextSelected, fontsReadyTick]);

  // eslint-disable-next-line no-unused-vars
  const downloadHolzLaser = async () => {
    const tempCanvas = document.createElement('canvas');
    await renderHolzPreview({ targetCanvas: tempCanvas, forLaser: true });
    if (!tempCanvas.width || !tempCanvas.height) return;
    const link = document.createElement('a');
    link.download = `laser-${selectedHolz?.name || 'holz-anhaenger'}.png`;
    link.href = tempCanvas.toDataURL('image/png');
    link.click();
  };

  const handleHolzCanvasMouseDown = (e) => {
    const canvas = holzCanvasRef.current;
    if (!canvas || !selectedHolz || !holzName) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    const baseWidth = selectedHolz.width || 100;
    const baseHeight = selectedHolz.height || 100;
    const targetWidth = 500;
    const scalePxPerMm = Math.min(targetWidth / baseWidth, targetWidth / baseHeight);

    const ctx = canvas.getContext('2d');
    const hit = getHolzTextHandleAtPoint({ canvasX, canvasY, ctx, scalePxPerMm });
    if (!hit) {
      setHolzTextSelected(false);
      return;
    }

    setHolzTextSelected(true);
    setHolzTextInteraction({
      dragging: hit.handle === 'move',
      resizing: hit.handle === 'resize-tr',
      handle: hit.handle
    });

    const centerX = hit.state?.bounds?.centerX ?? (hit.state?.x ?? canvasX);
    const centerY = hit.state?.bounds?.centerY ?? (hit.state?.y ?? canvasY);
    const startDist = Math.sqrt(Math.pow(canvasX - centerX, 2) + Math.pow(canvasY - centerY, 2)) || 1;

    holzTextDragStartRef.current = {
      x: canvasX,
      y: canvasY,
      centerX,
      centerY,
      startDist,
      initial: { ...holzTextTransform }
    };

    e.preventDefault();
  };

  const handleHolzCanvasMouseMove = (e) => {
    if (!holzTextInteraction.dragging && !holzTextInteraction.resizing) return;
    const canvas = holzCanvasRef.current;
    if (!canvas || !selectedHolz || !holzName) return;
    const start = holzTextDragStartRef.current;
    if (!start) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (e.clientX - rect.left) * scaleX;
    const canvasY = (e.clientY - rect.top) * scaleY;

    const baseWidth = selectedHolz.width || 100;
    const baseHeight = selectedHolz.height || 100;
    const targetWidth = 500;
    const scalePxPerMm = Math.min(targetWidth / baseWidth, targetWidth / baseHeight);
    const pxToMm = 1 / (scalePxPerMm || 1);

    if (holzTextInteraction.dragging) {
      const dxMm = (canvasX - start.x) * pxToMm;
      const dyMm = (canvasY - start.y) * pxToMm;
      setHolzTextTransform({
        ...start.initial,
        x: (start.initial.x || 0) + dxMm,
        y: (start.initial.y || 0) + dyMm
      });
      return;
    }

    if (holzTextInteraction.resizing) {
      const currentDist = Math.sqrt(Math.pow(canvasX - start.centerX, 2) + Math.pow(canvasY - start.centerY, 2)) || 1;
      const scaleFactor = currentDist / (start.startDist || 1);
      const nextScale = clamp((start.initial.scale || 1) * scaleFactor, 0.1, 5);
      setHolzTextTransform({
        ...start.initial,
        scale: nextScale
      });
    }
  };

  const stopHolzTextInteraction = () => {
    setHolzTextInteraction({ dragging: false, resizing: false, handle: null });
    holzTextDragStartRef.current = null;
  };

  const handleHolzCanvasTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    const touch = e.touches[0];
    handleHolzCanvasMouseDown({
      clientX: touch.clientX,
      clientY: touch.clientY,
      preventDefault: () => {},
    });
    e.preventDefault();
  };

  const handleHolzCanvasTouchMove = (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    const touch = e.touches[0];
    handleHolzCanvasMouseMove({
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    e.preventDefault();
  };


  // Bestellfunktionen
  const handleOrderClick = () => {
    setShowOrderModal(true);
  };

  // Hilfsfunktion: Aktuelle Bestellung vorbereiten
  const prepareCurrentOrder = async () => {
    let productType, productName, mockupImageFront, mockupImageBack, laserImageFront, laserImageBack, laserSvgFront, laserSvgBack, fileBaseName;

    const sanitizeFilenamePart = (value) => {
      return String(value || '')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[\\/:*?"<>|]+/g, '')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80);
    };

    const buildOrderBaseName = ({ ownerName, kind, namePart }) => {
      const owner = sanitizeFilenamePart(ownerName || 'Besitzer');
      const second = sanitizeFilenamePart(namePart || (kind === 'holz' ? 'Tier' : 'Text1'));
      return `${owner}_${second}`.replace(/_+/g, '_').slice(0, 120);
    };

    if (activeMenu === 'holz') {
      // Holzanhänger
      productType = 'Holzanhänger';
      productName = selectedHolz?.name || 'Holzanhänger';

      fileBaseName = buildOrderBaseName({
        ownerName: orderFormData.customerName,
        kind: 'holz',
        namePart: holzName || selectedHolz?.name || productName
      });

      // Mockup-Bild generieren
      const mockupCanvas = document.createElement('canvas');
      await renderHolzPreview({ targetCanvas: mockupCanvas, forLaser: false });
      mockupImageFront = mockupCanvas.toDataURL('image/jpeg', 0.9);

      // Laser-Bild generieren
      const laserCanvas = document.createElement('canvas');
      await renderHolzPreview({ targetCanvas: laserCanvas, forLaser: true });
      laserImageFront = laserCanvas.toDataURL('image/png');
      laserSvgFront = await buildHolzLaserSvg();
    } else {
      // Anhängergravur
      productType = 'Anhängergravur';
      productName = selectedRohling?.name || 'Rohling';

      const text1Raw = textConfig?.front?.[0]?.text || '';
      const text1 = String(text1Raw).split('\n').map(s => s.trim()).filter(Boolean)[0] || productName;
      fileBaseName = buildOrderBaseName({
        ownerName: orderFormData.customerName,
        kind: 'dogtag',
        namePart: text1
      });

      // Mockup-Bilder
      if (canvasRefFront.current) {
        mockupImageFront = canvasRefFront.current.toDataURL('image/jpeg', 0.9);
      }
      if (canvasRefBack.current && selectedRohling?.beidseitig) {
        mockupImageBack = canvasRefBack.current.toDataURL('image/jpeg', 0.9);
      }

      // Laser-Bilder generieren
      laserImageFront = await generateLaserImageData('front');
      laserSvgFront = (await buildDogtagLaserSvg('front')) || buildLaserSvgFromPng(laserImageFront);

      if (selectedRohling?.beidseitig) {
        laserImageBack = await generateLaserImageData('back');
        laserSvgBack = (await buildDogtagLaserSvg('back')) || buildLaserSvgFromPng(laserImageBack);
      }
    }

    return {
      productType,
      productName,
      mockupImageFront,
      mockupImageBack,
      laserImageFront,
      laserImageBack,
      laserSvgFront: laserSvgFront || null,
      laserSvgBack: laserSvgBack || null,
      fileBaseName: fileBaseName || null
    };
  };

  // Funktion: Weitere Bestellung hinzufügen
  const handleAddAnotherOrder = async () => {
    if (!orderFormData.customerName || !orderFormData.customerEmail) {
      showMessage('error', 'Bitte Name und E-Mail-Adresse eingeben');
      return;
    }

    setOrderLoading(true);
    try {
      const orderData = await prepareCurrentOrder();

      // Bestellung zur Liste hinzufügen
      setSavedOrders(prev => [...prev, orderData]);
      showMessage('success', 'Rohling zur Bestellung hinzugefügt!');

      // Aktuellen Rohling zurücksetzen
      if (activeMenu === 'holz') {
        setHolzName('');
        setHolzFont('');
        setHolzStyle({ bold: false, italic: false });
        setSelectedHolz(null);
        setSelectedHolzSvgIndex(0);
        setHolzTextTransform({ x: 0, y: 0, scale: 1 });
        setHolzTextSelected(false);
        setHolzTextInteraction({ dragging: false, resizing: false, handle: null });
      } else {
        setTextConfig({
          front: [
            { id: '1', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
            { id: '2', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
            { id: '3', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
          ],
          back: [
            { id: '1', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
            { id: '2', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
            { id: '3', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
          ]
        });
        setImages({ front: [], back: [] });
        setSelectedRohling(null);
        setSelectedFarbvariante(null);
      }

      // Modal schließen
      setShowOrderModal(false);
    } catch (error) {
      console.error('Fehler beim Hinzufügen:', error);
      showMessage('error', 'Fehler beim Hinzufügen: ' + error.message);
    } finally {
      setOrderLoading(false);
    }
  };

  // Funktion: Alle Bestellungen absenden
  const handleSubmitAllOrders = async (e) => {
    e.preventDefault();

    if (!orderFormData.customerName || !orderFormData.customerEmail) {
      showMessage('error', 'Bitte Name und E-Mail-Adresse eingeben');
      return;
    }

    if (!orderFormData.confirmOrder || !orderFormData.acceptPrivacy) {
      showMessage('error', 'Bitte bestätigen Sie die Bestellung und akzeptieren die Datenschutzerklärung');
      return;
    }

    setOrderLoading(true);

    try {
      // Aktuelle Bestellung auch hinzufügen
      const currentOrder = await prepareCurrentOrder();
      const allOrders = [...savedOrders, currentOrder];

      // Multi-Order-Daten erstellen
      const orderData = {
        customerName: orderFormData.customerName,
        customerEmail: orderFormData.customerEmail,
        isExistingCustomer: orderFormData.isExistingCustomer,
        orders: allOrders
      };

      // Adresse nur bei Neukunden hinzufügen
      if (!orderFormData.isExistingCustomer) {
        orderData.street = orderFormData.street;
        orderData.postalCode = orderFormData.postalCode;
        orderData.city = orderFormData.city;
      }

      const response = await fetch('/api/order-multi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      if (response.ok) {
        showMessage('success', result.message || 'Bestellungen erfolgreich versendet!');

        // Alles zurücksetzen
        setSavedOrders([]);
        if (activeMenu === 'holz') {
          setHolzName('');
          setHolzFont('');
          setHolzStyle({ bold: false, italic: false });
          setSelectedHolz(null);
          setSelectedHolzSvgIndex(0);
          setHolzTextTransform({ x: 0, y: 0, scale: 1 });
          setHolzTextSelected(false);
          setHolzTextInteraction({ dragging: false, resizing: false, handle: null });
        } else {
          setTextConfig({
            front: [
              { id: '1', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
              { id: '2', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
              { id: '3', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
            ],
            back: [
              { id: '1', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
              { id: '2', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false },
              { id: '3', text: '', font: 'Arial', size: 48, x: 0, y: 0, scale: 1, rotation: 0, alignHorizontal: 'center', alignVertical: 'center', bold: false, italic: false, underline: false }
            ]
          });
          setImages({ front: [], back: [] });
          setSelectedRohling(null);
          setSelectedFarbvariante(null);
        }

        setShowOrderModal(false);
        setOrderFormData({
          customerName: '',
          customerEmail: '',
          isExistingCustomer: true,
          street: '',
          postalCode: '',
          city: '',
          confirmOrder: false,
          acceptPrivacy: false
        });
      } else {
        showMessage('error', result.error || 'Fehler beim Versenden der Bestellung');
      }
    } catch (error) {
      console.error('Bestellfehler:', error);
      showMessage('error', 'Fehler beim Versenden der Bestellung: ' + error.message);
    } finally {
      setOrderLoading(false);
    }
  };

  const pageTitle = activeMenu === 'anhaengergravur'
    ? 'Anhängergravur'
    : activeMenu === 'holz'
      ? 'Christbaumanhänger Holz'
      : '';

  if (loading) {
    return <div className="loading">Lade Daten...</div>;
  }

  return (
    <div className="container user-view">
      {pageTitle && <h1 className="page-title">{pageTitle}</h1>}

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {!activeMenu && (
        <div className="card product-menu">
          <div className="menu-grid">
            <button
              type="button"
              className="menu-tile menu-tile-primary"
              onClick={() => setActiveMenu('anhaengergravur')}
            >
              <div className="menu-icon">
                <FontAwesomeIcon icon={['fas', 'gem']} />
              </div>
              <h3>Anhängergravur</h3>
            </button>
            {!hideHolzMenuOnThisHost && (
              <button
                type="button"
                className="menu-tile menu-tile-secondary"
                onClick={() => setActiveMenu('holz')}
              >
              <div className="menu-icon">
                <FontAwesomeIcon icon={['fas', 'tree']} />
              </div>
              <h3>Christbaumanhänger Holz</h3>
              </button>
            )}
          </div>
        </div>
      )}

      {activeMenu === 'anhaengergravur' && !selectedRohling && (
        <div className="rohling-selection">
          <button className="btn btn-secondary back-button" onClick={resetToMenu}>
            ← Zurück zum Hauptmenü
          </button>
          <h2>Wählen Sie einen Rohling aus</h2>
      <div className="rohlinge-grid">
        {rohlinge.length === 0 ? (
          <div className="empty-state">
            Keine Rohlinge verfügbar. Bitte wenden Sie sich an den Administrator.
          </div>
            ) : (
              rohlinge.map(rohling => {
                const firstFarbvariante = rohling.farbvarianten && rohling.farbvarianten[0];
                return (
                  <div
                    key={rohling.id}
                    className="rohling-card"
                    onClick={() => handleRohlingSelect(rohling)}
                  >
                    <div className="rohling-image">
                      {rohling.masterSvg ? (
                        <img
                          src={buildAssetUrl(rohling.masterSvg)}
                          alt={rohling.name}
                        />
                      ) : firstFarbvariante && firstFarbvariante.frontImage ? (
                        <img
                          src={buildAssetUrl(firstFarbvariante.frontImage)}
                          alt={rohling.name}
                        />
                      ) : (
                        <div className="empty-state" style={{ padding: '1rem' }}>Keine Vorschau</div>
                      )}
                    </div>
                    <div className="rohling-info">
                      <h3>{rohling.name}</h3>
                      <p>{rohling.breite}mm × {rohling.hoehe}mm</p>
                      <p className="rohling-type">
                        {rohling.beidseitig ? 'Beidseitig' : 'Einseitig'}
                      </p>
                      {rohling.farbvarianten && rohling.farbvarianten.length > 1 && (
                        <p className="color-count">{rohling.farbvarianten.length} Farben verfügbar</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {activeMenu === 'anhaengergravur' && selectedRohling && (
        <div className="design-area">
          <button
            className="btn btn-secondary back-button"
            onClick={() => setSelectedRohling(null)}
          >
            ← Zurück zur Auswahl
          </button>

          {/* Rohling-Info ganz oben */}
          <div className="card">
            <h2>Ausgewählter Rohling</h2>
            <div className="selected-rohling-info">
              <h3>{selectedRohling.name}</h3>
              <p>Größe: {selectedRohling.breite}mm × {selectedRohling.hoehe}mm</p>
              {selectedFarbvariante && <p>Farbe: {selectedFarbvariante.farbe}</p>}

              {selectedRohling.farbvarianten && selectedRohling.farbvarianten.length > 1 && (
                <div className="color-variants">
                  <label><strong>Farbe wählen:</strong></label>
                  <div className="color-variant-images">
                    {selectedRohling.farbvarianten.map(farbvariante => (
                      <div
                        key={farbvariante.id}
                        className={`color-variant-item ${selectedFarbvariante && farbvariante.id === selectedFarbvariante.id ? 'active' : ''}`}
                        onClick={() => handleColorChange(farbvariante)}
                        title={farbvariante.farbe}
                      >
                        {renderVariantPreview(farbvariante)}
                        <span>{farbvariante.farbe}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Vorderseite: Vorschau + Einstellungen */}
          <div className="card">
            <h2>Vorderseite</h2>

            {/* Vorschau */}
            <div className="preview-container">
              <canvas
                ref={canvasRefFront}
                className="preview-canvas"
                onMouseDown={(e) => handleCanvasMouseDown('front', e)}
                onMouseMove={(e) => handleCanvasMouseMove('front', e)}
                onMouseUp={() => handleCanvasMouseUp('front')}
                onMouseLeave={() => handleCanvasMouseUp('front')}
                onTouchStart={(e) => handleCanvasTouchStart('front', e)}
                onTouchMove={(e) => handleCanvasTouchMove('front', e)}
                onTouchEnd={() => handleCanvasTouchEnd('front')}
                style={{
                  cursor: selectedImageId.front
                    ? (draggingImage.front ? 'grabbing' : 'grab')
                    : 'default',
                  touchAction: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '5px', marginTop: '10px', flexWrap: 'wrap' }}>
              <button
                className={`btn btn-sm ${showGrid.front ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setShowGrid(prev => ({ ...prev, front: !prev.front }))}
                style={{ flex: '1 1 100%' }}
                title="mm-Raster ein-/ausblenden"
              >
                Raster
              </button>
            </div>

            <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />

            {/* Einstellungen Vorderseite */}
            <h3>Text-Einstellungen</h3>

            {/* Horizontal layout for all 3 text sections */}
            <div className="text-grid-horizontal">
              {/* Text 1 */}
              <div
                className={`text-item-compact ${selectedTextIndex.front === 0 ? 'selected' : ''}`}
                style={{ display: visibleTextFields.front[0] ? 'block' : 'none' }}
              >
                <label>Gravur-Text 1</label>
                <textarea
                  ref={textareaRefs.front[0]}
                  value={textConfig.front[0].text}
                  onChange={(e) => handleTextConfigChange('front', 0, 'text', e.target.value)}
                  placeholder="Text 1"
                  rows="1"
                />
                <select
                  value={textConfig.front[0].font}
                  onChange={(e) => handleTextConfigChange('front', 0, 'font', e.target.value)}
                  className="font-select-compact"
                >
                  {schriftarten.map(schrift => (
                    <option key={schrift.id} value={schrift.name} style={{fontFamily: getFontStack(schrift.name)}}>
                      {schrift.name}
                    </option>
                  ))}
                </select>
                <div className="text-controls-compact">
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 0, 'bold') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 0, 'bold')}
                    title="Fett"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 0, 'italic') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 0, 'italic')}
                    title="Kursiv"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 0, 'underline') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 0, 'underline')}
                    title="Unterstrichen"
                  >
                    <u>U</u>
                  </button>
                  <button
                    type="button"
                    className="btn btn-center"
                    onClick={() => {
                      handleTextConfigChange('front', 0, 'alignHorizontal', 'center');
                      handleTextConfigChange('front', 0, 'alignVertical', 'center');
                    }}
                    title="Text zentrieren"
                  >
                    Mitte
                  </button>
                </div>
              </div>

              {/* Text 2 */}
              <div
                className={`text-item-compact ${selectedTextIndex.front === 1 ? 'selected' : ''}`}
                style={{ display: visibleTextFields.front[1] ? 'block' : 'none' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Gravur-Text 2</label>
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => hideTextField('front', 1)}
                    style={{ padding: 0 }}
                  >
                    Textfeld schließen
                  </button>
                </div>
                <textarea
                  ref={textareaRefs.front[1]}
                  value={textConfig.front[1].text}
                  onChange={(e) => handleTextConfigChange('front', 1, 'text', e.target.value)}
                  placeholder="Text 2"
                  rows="1"
                />
                <select
                  value={textConfig.front[1].font}
                  onChange={(e) => handleTextConfigChange('front', 1, 'font', e.target.value)}
                  className="font-select-compact"
                >
                  {schriftarten.map(schrift => (
                    <option key={schrift.id} value={schrift.name} style={{fontFamily: getFontStack(schrift.name)}}>
                      {schrift.name}
                    </option>
                  ))}
                </select>
                <div className="text-controls-compact">
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 1, 'bold') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 1, 'bold')}
                    title="Fett"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 1, 'italic') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 1, 'italic')}
                    title="Kursiv"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 1, 'underline') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 1, 'underline')}
                    title="Unterstrichen"
                  >
                    <u>U</u>
                  </button>
                  <button
                    type="button"
                    className="btn btn-center"
                    onClick={() => {
                      handleTextConfigChange('front', 1, 'alignHorizontal', 'center');
                      handleTextConfigChange('front', 1, 'alignVertical', 'center');
                    }}
                    title="Text zentrieren"
                  >
                    Mitte
                  </button>
                </div>
              </div>

              {/* Text 3 */}
              <div
                className={`text-item-compact ${selectedTextIndex.front === 2 ? 'selected' : ''}`}
                style={{ display: visibleTextFields.front[2] ? 'block' : 'none' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Gravur-Text 3</label>
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => hideTextField('front', 2)}
                    style={{ padding: 0 }}
                  >
                    Textfeld schließen
                  </button>
                </div>
                <textarea
                  ref={textareaRefs.front[2]}
                  value={textConfig.front[2].text}
                  onChange={(e) => handleTextConfigChange('front', 2, 'text', e.target.value)}
                  placeholder="Text 3"
                  rows="1"
                />
                <select
                  value={textConfig.front[2].font}
                  onChange={(e) => handleTextConfigChange('front', 2, 'font', e.target.value)}
                  className="font-select-compact"
                >
                  {schriftarten.map(schrift => (
                    <option key={schrift.id} value={schrift.name} style={{fontFamily: getFontStack(schrift.name)}}>
                      {schrift.name}
                    </option>
                  ))}
                </select>
                <div className="text-controls-compact">
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 2, 'bold') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 2, 'bold')}
                    title="Fett"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 2, 'italic') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 2, 'italic')}
                    title="Kursiv"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isFormatActive('front', 2, 'underline') ? 'active' : ''}`}
                    onClick={() => toggleFormatting('front', 2, 'underline')}
                    title="Unterstrichen"
                  >
                    <u>U</u>
                  </button>
                  <button
                    type="button"
                    className="btn btn-center"
                    onClick={() => {
                      handleTextConfigChange('front', 2, 'alignHorizontal', 'center');
                      handleTextConfigChange('front', 2, 'alignVertical', 'center');
                    }}
                    title="Text zentrieren"
                  >
                    Mitte
                  </button>
                </div>
              </div>
            </div>

            <div style={{ margin: '10px 0 0 0' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => showNextTextField('front')}
                disabled={!visibleTextFields.front.some((visible, idx) => idx > 0 && !visible)}
              >
                Weiteres Textfeld
              </button>
            </div>

            <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />

            <h3>Bilder hinzufügen</h3>

            <div className="form-group">
              <label>Bild hinzufügen (klicken zum Hinzufügen)</label>
              {bilder.length === 0 ? (
                <p style={{ fontSize: '14px', color: '#666' }}>
                  Keine Bilder verfügbar. Bitte laden Sie zuerst Bilder im Admin-Bereich hoch.
                </p>
              ) : (
                <div className="bilder-preview-grid">
                  {bilder.map(bild => (
                    <div
                      key={bild.id}
                      className="bild-preview-item"
                      onClick={() => addImage('front', bild.id)}
                      title={`${bild.name} hinzufügen`}
                      style={{ cursor: 'pointer' }}
                    >
                      {bild.type === 'fontawesome' ? (
                        <FontAwesomeIcon
                          icon={[
                            bild.iconStyle === 'brands' ? 'fab' : bild.iconStyle === 'regular' ? 'far' : 'fas',
                            bild.iconName
                          ]}
                          className="icon-preview"
                        />
                      ) : (
                        <img
                          src={buildAssetUrl(bild.path)}
                          alt={bild.name}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {images.front.length > 0 && (
              <>
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Hinzugefügte Bilder:</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {images.front.map((img) => {
                      const bild = bilder.find(b => b.id === img.bildId);
                      if (!bild) return null;
                      const isSelected = selectedImageId.front === img.id;

                      return (
                        <div
                          key={img.id}
                          style={{
                            padding: '6px 12px',
                            border: isSelected ? '2px solid #3498db' : '1px solid #ddd',
                            borderRadius: '6px',
                            backgroundColor: isSelected ? '#e8f4fd' : '#f8f9fa',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          onClick={() => setSelectedImageId(prev => ({ ...prev, front: img.id }))}
                        >
                          {bild.name}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Rückseite: Vorschau + Einstellungen */}
          {selectedRohling.beidseitig && (
            <div className="card">
              <h2>Rückseite</h2>

              {/* Vorschau */}
              <div className="preview-container">
                <canvas
                  ref={canvasRefBack}
                  className="preview-canvas"
                  onMouseDown={(e) => handleCanvasMouseDown('back', e)}
                  onMouseMove={(e) => handleCanvasMouseMove('back', e)}
                  onMouseUp={() => handleCanvasMouseUp('back')}
                  onMouseLeave={() => handleCanvasMouseUp('back')}
                  onTouchStart={(e) => handleCanvasTouchStart('back', e)}
                  onTouchMove={(e) => handleCanvasTouchMove('back', e)}
                  onTouchEnd={() => handleCanvasTouchEnd('back')}
                  style={{
                    cursor: selectedImageId.back
                      ? (draggingImage.back ? 'grabbing' : 'grab')
                      : 'default',
                    touchAction: 'none'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '5px', marginTop: '10px', flexWrap: 'wrap' }}>
                <button
                  className={`btn btn-sm ${showGrid.back ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setShowGrid(prev => ({ ...prev, back: !prev.back }))}
                  style={{ flex: '1 1 100%' }}
                  title="mm-Raster ein-/ausblenden"
                >
                  Raster
                </button>
              </div>

              <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />

              {/* Einstellungen Rückseite */}
              <h3>Text-Einstellungen</h3>

              {/* Horizontal layout for all 3 text sections */}
              <div className="text-grid-horizontal">
                {/* Text 1 */}
                <div
                  className={`text-item-compact ${selectedTextIndex.back === 0 ? 'selected' : ''}`}
                  style={{ display: visibleTextFields.back[0] ? 'block' : 'none' }}
                >
                  <label>Gravur-Text 1</label>
                  <textarea
                    ref={textareaRefs.back[0]}
                    value={textConfig.back[0].text}
                    onChange={(e) => handleTextConfigChange('back', 0, 'text', e.target.value)}
                    placeholder="Text 1"
                    rows="1"
                  />
                  <select
                    value={textConfig.back[0].font}
                    onChange={(e) => handleTextConfigChange('back', 0, 'font', e.target.value)}
                    className="font-select-compact"
                  >
                    {schriftarten.map(schrift => (
                      <option key={schrift.id} value={schrift.name} style={{fontFamily: getFontStack(schrift.name)}}>
                        {schrift.name}
                      </option>
                    ))}
                  </select>
                  <div className="text-controls-compact">
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 0, 'bold') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 0, 'bold')}
                      title="Fett"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 0, 'italic') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 0, 'italic')}
                      title="Kursiv"
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 0, 'underline') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 0, 'underline')}
                      title="Unterstrichen"
                    >
                      <u>U</u>
                    </button>
                    <button
                      type="button"
                      className="btn btn-center"
                      onClick={() => {
                        handleTextConfigChange('back', 0, 'alignHorizontal', 'center');
                        handleTextConfigChange('back', 0, 'alignVertical', 'center');
                      }}
                      title="Text zentrieren"
                    >
                      Mitte
                    </button>
                  </div>
                </div>

                {/* Text 2 */}
                <div
                  className={`text-item-compact ${selectedTextIndex.back === 1 ? 'selected' : ''}`}
                  style={{ display: visibleTextFields.back[1] ? 'block' : 'none' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Gravur-Text 2</label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => hideTextField('back', 1)}
                      style={{ padding: 0 }}
                    >
                      Textfeld schließen
                    </button>
                  </div>
                  <textarea
                    ref={textareaRefs.back[1]}
                    value={textConfig.back[1].text}
                    onChange={(e) => handleTextConfigChange('back', 1, 'text', e.target.value)}
                    placeholder="Text 2"
                    rows="1"
                  />
                  <select
                    value={textConfig.back[1].font}
                    onChange={(e) => handleTextConfigChange('back', 1, 'font', e.target.value)}
                    className="font-select-compact"
                  >
                    {schriftarten.map(schrift => (
                      <option key={schrift.id} value={schrift.name} style={{fontFamily: getFontStack(schrift.name)}}>
                        {schrift.name}
                      </option>
                    ))}
                  </select>
                  <div className="text-controls-compact">
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 1, 'bold') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 1, 'bold')}
                      title="Fett"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 1, 'italic') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 1, 'italic')}
                      title="Kursiv"
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 1, 'underline') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 1, 'underline')}
                      title="Unterstrichen"
                    >
                      <u>U</u>
                    </button>
                    <button
                      type="button"
                      className="btn btn-center"
                      onClick={() => {
                        handleTextConfigChange('back', 1, 'alignHorizontal', 'center');
                        handleTextConfigChange('back', 1, 'alignVertical', 'center');
                      }}
                      title="Text zentrieren"
                    >
                      Mitte
                    </button>
                  </div>
                </div>

                {/* Text 3 */}
                <div
                  className={`text-item-compact ${selectedTextIndex.back === 2 ? 'selected' : ''}`}
                  style={{ display: visibleTextFields.back[2] ? 'block' : 'none' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label>Gravur-Text 3</label>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => hideTextField('back', 2)}
                      style={{ padding: 0 }}
                    >
                      Textfeld schließen
                    </button>
                  </div>
                  <textarea
                    ref={textareaRefs.back[2]}
                    value={textConfig.back[2].text}
                    onChange={(e) => handleTextConfigChange('back', 2, 'text', e.target.value)}
                    placeholder="Text 3"
                    rows="1"
                  />
                  <select
                    value={textConfig.back[2].font}
                    onChange={(e) => handleTextConfigChange('back', 2, 'font', e.target.value)}
                    className="font-select-compact"
                  >
                    {schriftarten.map(schrift => (
                      <option key={schrift.id} value={schrift.name} style={{fontFamily: getFontStack(schrift.name)}}>
                        {schrift.name}
                      </option>
                    ))}
                  </select>
                  <div className="text-controls-compact">
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 2, 'bold') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 2, 'bold')}
                      title="Fett"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 2, 'italic') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 2, 'italic')}
                      title="Kursiv"
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      className={`btn btn-style-compact ${isFormatActive('back', 2, 'underline') ? 'active' : ''}`}
                      onClick={() => toggleFormatting('back', 2, 'underline')}
                      title="Unterstrichen"
                    >
                      <u>U</u>
                    </button>
                    <button
                      type="button"
                      className="btn btn-center"
                      onClick={() => {
                        handleTextConfigChange('back', 2, 'alignHorizontal', 'center');
                        handleTextConfigChange('back', 2, 'alignVertical', 'center');
                      }}
                      title="Text zentrieren"
                    >
                      Mitte
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ margin: '10px 0 0 0' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => showNextTextField('back')}
                  disabled={!visibleTextFields.back.some((visible, idx) => idx > 0 && !visible)}
                >
                  Weiteres Textfeld
                </button>
              </div>

              <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />

              <h3>Bilder hinzufügen</h3>

              <div className="form-group">
                <label>Bild hinzufügen (klicken zum Hinzufügen)</label>
                {bilder.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Keine Bilder verfügbar. Bitte laden Sie zuerst Bilder im Admin-Bereich hoch.
                  </p>
                ) : (
                  <div className="bilder-preview-grid">
                    {bilder.map(bild => (
                      <div
                        key={bild.id}
                        className="bild-preview-item"
                        onClick={() => addImage('back', bild.id)}
                        title={`${bild.name} hinzufügen`}
                        style={{ cursor: 'pointer' }}
                      >
                        {bild.type === 'fontawesome' ? (
                          <FontAwesomeIcon
                            icon={[
                              bild.iconStyle === 'brands' ? 'fab' : bild.iconStyle === 'regular' ? 'far' : 'fas',
                              bild.iconName
                            ]}
                            className="icon-preview"
                          />
                        ) : (
                          <img
                            src={buildAssetUrl(bild.path)}
                            alt={bild.name}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {images.back.length > 0 && (
                <>
                  <div className="form-group" style={{ marginTop: '15px' }}>
                    <label>Hinzugefügte Bilder:</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {images.back.map((img) => {
                        const bild = bilder.find(b => b.id === img.bildId);
                        if (!bild) return null;
                        const isSelected = selectedImageId.back === img.id;

                        return (
                          <div
                            key={img.id}
                            style={{
                              padding: '6px 12px',
                              border: isSelected ? '2px solid #3498db' : '1px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: isSelected ? '#e8f4fd' : '#f8f9fa',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            onClick={() => setSelectedImageId(prev => ({ ...prev, back: img.id }))}
                          >
                            {bild.name}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Bestellung aufgeben */}
          <div className="card">
            <h2>Bestellung</h2>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Sind Sie mit Ihrem Entwurf zufrieden? Klicken Sie auf "Bestellen", um Ihre Gravur kostenpflichtig zu bestellen.
            </p>
            <button
              className="btn btn-success"
              onClick={handleOrderClick}
              style={{ width: '100%' }}
            >
              Jetzt bestellen
            </button>
          </div>
        </div>
      )}

      {activeMenu === 'holz' && !selectedHolzKategorie && (
        <div className="rohling-selection">
          <button className="btn btn-secondary back-button" onClick={resetToMenu}>
            ← Zurück zum Hauptmenü
          </button>
          <h2>Kategorie auswählen</h2>
          <div className="kategorie-grid">
            {['Hunde', 'Katzen', 'Kaninchen'].map(kategorie => {
              const count = holzAnhaenger.filter(item => item.kategorie === kategorie).length;
              return (
                <div
                  key={kategorie}
                  className={`kategorie-card ${count === 0 ? 'disabled' : ''}`}
                  onClick={() => count > 0 && setSelectedHolzKategorie(kategorie)}
                >
                  <h3>{kategorie}</h3>
                  <p>{count} Anhänger verfügbar</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeMenu === 'holz' && selectedHolzKategorie && !selectedHolz && (
        <div className="rohling-selection">
          <button className="btn btn-secondary back-button" onClick={() => setSelectedHolzKategorie(null)}>
            ← Zurück zu Kategorien
          </button>
          <h2>Anhänger auswählen ({selectedHolzKategorie})</h2>
          <div className="rohlinge-grid">
            {holzAnhaenger.filter(item => item.kategorie === selectedHolzKategorie).length === 0 ? (
              <div className="empty-state">
                Keine Anhänger in dieser Kategorie verfügbar.
              </div>
            ) : (
              holzAnhaenger
                .filter(item => item.kategorie === selectedHolzKategorie)
                .map(item => (
                  <div
                    key={item.id}
                    className="rohling-card"
                    onClick={() => handleHolzSelect(item)}
                  >
                    <div className="rohling-image">
                      {item.svgPath ? (
                        <img
                          src={holzPreviewCache[item.id] || buildAssetUrl(item.svgPath)}
                          alt={item.name}
                          style={{ width: '100%', height: 160, objectFit: 'contain' }}
                        />
                      ) : (
                        <div className="empty-state" style={{ padding: '1rem' }}>Keine Vorschau</div>
                      )}
                    </div>
                    <div className="rohling-info">
                      <h3>{item.name}</h3>
                      <p>{item.width}mm × {item.height}mm</p>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      )}

      {activeMenu === 'holz' && selectedHolz && (
        <div className="design-area">
          <button className="btn btn-secondary back-button" onClick={resetHolzSelection}>
            ← Zurück zur Auswahl
          </button>

          <div className="card">
            <h2>{selectedHolz.name}</h2>
            {(() => {
              const svgPaths = getHolzSvgPaths(selectedHolz);
              if (svgPaths.length <= 1) return null;
              const safeIdx = Math.min(Math.max(selectedHolzSvgIndex || 0, 0), svgPaths.length - 1);
              return (
                <div className="form-group" style={{ marginTop: '10px' }}>
                  <label>Version</label>
                  <select
                    value={safeIdx}
                    onChange={(e) => setSelectedHolzSvgIndex(parseInt(e.target.value, 10) || 0)}
                    style={{ width: '100%' }}
                  >
                    {svgPaths.map((path, idx) => (
                      <option key={`${idx}-${path}`} value={idx}>Version {idx + 1}</option>
                    ))}
                  </select>
                </div>
              );
            })()}
            <p>Größe: {selectedHolz.width}mm × {selectedHolz.height}mm</p>
          </div>

          <div className="card">
            <h3>Vorschau & Laserbild</h3>
            <div className="preview-container">
              <canvas
                ref={holzCanvasRef}
                className="preview-canvas"
                onMouseDown={handleHolzCanvasMouseDown}
                onMouseMove={handleHolzCanvasMouseMove}
                onMouseUp={stopHolzTextInteraction}
                onMouseLeave={stopHolzTextInteraction}
                onTouchStart={handleHolzCanvasTouchStart}
                onTouchMove={handleHolzCanvasTouchMove}
                onTouchEnd={stopHolzTextInteraction}
                style={{ touchAction: 'none' }}
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <button
                className="btn btn-success"
                onClick={handleOrderClick}
                style={{ width: '100%' }}
              >
                Jetzt bestellen
              </button>
            </div>
          </div>

          <div className="card">
            <h3>Name</h3>
            {selectedHolz.farben && selectedHolz.farben.length > 0 && (
              <div className="form-group">
                <label>Farbe wählen</label>
                <div className="color-variant-images">
                  {selectedHolz.farben.map(farbe => (
                    <div
                      key={farbe.id}
                      className={`color-variant-item ${selectedHolzColor === farbe.id ? 'active' : ''}`}
                      onClick={() => setSelectedHolzColor(farbe.id)}
                      title={farbe.name}
                    >
                      <span
                        className="color-dot"
                        style={{ backgroundColor: farbe.hexColor }}
                        aria-label={farbe.name}
                      />
                      <span>{farbe.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <input
              type="text"
              value={holzName}
              onChange={(e) => setHolzName(e.target.value)}
              placeholder="Name eintragen"
              style={{ width: '100%', marginBottom: '10px' }}
            />
            <div className="form-row" style={{ marginBottom: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setHolzTextTransform({ x: 0, y: 0, scale: 1 });
                  setHolzTextSelected(false);
                }}
                disabled={!holzName}
                style={{ width: '100%' }}
                title="Position und Größe zurücksetzen"
              >
                Text zurücksetzen
              </button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Schriftart</label>
                <select
                  value={holzFont}
                  onChange={(e) => setHolzFont(e.target.value)}
                  style={{ fontFamily: getFontStack(holzFont || 'Arial') }}
                >
                  {schriftarten.map(s => (
                    <option key={s.id} value={s.name} style={{ fontFamily: getFontStack(s.name) }}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Stil</label>
                <div className="text-controls-compact">
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isHolzFormatActive('bold') ? 'active' : ''}`}
                    onClick={() => toggleHolzFormatting('bold')}
                    title="Fett"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-style-compact ${isHolzFormatActive('italic') ? 'active' : ''}`}
                    onClick={() => toggleHolzFormatting('italic')}
                    title="Kursiv"
                  >
                    <em>I</em>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Bestellformular-Modal */}
      {showOrderModal && (
        <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Bestellung aufgeben</h2>
            <p>Bitte bestätigen Sie, dass Sie mit Ihrem Entwurf zufrieden sind und das Teil kostenpflichtig bestellen möchten.</p>

            {savedOrders.length > 0 && (
              <div className="saved-orders-summary" style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f8ff', borderRadius: '8px' }}>
                <h3 style={{ marginTop: 0 }}>Bereits hinzugefügte Rohlinge ({savedOrders.length})</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {savedOrders.map((order, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '0.75rem',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #ddd'
                      }}
                    >
                      {/* Vorschaubild */}
                      <div style={{ flexShrink: 0 }}>
                        <img
                          src={order.mockupImageFront}
                          alt={order.productName}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'contain',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            backgroundColor: '#f9f9f9'
                          }}
                        />
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                          {order.productName}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>
                          {order.productType}
                        </div>
                      </div>

                      {/* Löschen-Button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveSavedOrder(index)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                      >
                        Entfernen
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmitAllOrders}>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={orderFormData.isExistingCustomer}
                    onChange={(e) => setOrderFormData({ ...orderFormData, isExistingCustomer: e.target.checked })}
                    disabled={orderLoading}
                  />
                  <span>Ich bin Kunde/Kundin der Tierarztpraxis</span>
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="customerName">Name *</label>
                <input
                  type="text"
                  id="customerName"
                  value={orderFormData.customerName}
                  onChange={(e) => setOrderFormData({ ...orderFormData, customerName: e.target.value })}
                  required
                  disabled={orderLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="customerEmail">E-Mail-Adresse *</label>
                <input
                  type="email"
                  id="customerEmail"
                  value={orderFormData.customerEmail}
                  onChange={(e) => setOrderFormData({ ...orderFormData, customerEmail: e.target.value })}
                  required
                  disabled={orderLoading}
                />
              </div>

              {!orderFormData.isExistingCustomer && (
                <>
                  <div className="form-group">
                    <label htmlFor="street">Straße und Hausnummer *</label>
                    <input
                      type="text"
                      id="street"
                      value={orderFormData.street}
                      onChange={(e) => setOrderFormData({ ...orderFormData, street: e.target.value })}
                      required
                      disabled={orderLoading}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="postalCode">PLZ *</label>
                      <input
                        type="text"
                        id="postalCode"
                        value={orderFormData.postalCode}
                        onChange={(e) => setOrderFormData({ ...orderFormData, postalCode: e.target.value })}
                        required
                        disabled={orderLoading}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="city">Ort *</label>
                      <input
                        type="text"
                        id="city"
                        value={orderFormData.city}
                        onChange={(e) => setOrderFormData({ ...orderFormData, city: e.target.value })}
                        required
                        disabled={orderLoading}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={orderFormData.acceptPrivacy}
                    onChange={(e) => setOrderFormData({ ...orderFormData, acceptPrivacy: e.target.checked })}
                    disabled={orderLoading}
                    required
                  />
                  <span>
                    Ich habe die Hinweise zum{' '}
                    <a
                      href="/datenschutz"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        // Speichere Formulardaten im sessionStorage
                        sessionStorage.setItem('orderFormData', JSON.stringify(orderFormData));
                        window.open('/datenschutz', '_blank');
                      }}
                    >
                      Datenschutz
                    </a>
                    {' '}gelesen und verstanden. *
                  </span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={orderFormData.confirmOrder}
                    onChange={(e) => setOrderFormData({ ...orderFormData, confirmOrder: e.target.checked })}
                    disabled={orderLoading}
                    required
                  />
                  <span>Ich habe den Entwurf geprüft und möchte die Gravur verbindlich und kostenpflichtig bestellen. *</span>
                </label>
              </div>

              <div className="modal-buttons">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowOrderModal(false)}
                  disabled={orderLoading}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleAddAnotherOrder}
                  disabled={orderLoading}
                >
                  {orderLoading ? 'Wird gespeichert...' : 'Weiteren Rohling hinzufügen'}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={orderLoading}
                >
                  {orderLoading ? 'Wird versendet...' : savedOrders.length > 0 ? `Alle ${savedOrders.length + 1} Rohlinge bestellen` : 'Jetzt bestellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserView;
